import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import { Avatar, TextField, Button, IconButton, Menu, MenuItem, Typography, Box, Paper } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RepeatIcon from '@mui/icons-material/Repeat';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareIcon from '@mui/icons-material/Share';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import moment from 'moment-timezone';
import 'moment/locale/ja';
import './Post.css';
import { useAuth } from '../contexts/AuthContext';
import LockIcon from '@mui/icons-material/Lock';
import ReplyIcon from '@mui/icons-material/Reply';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CircularProgress from '@mui/material/CircularProgress';
import config from '../config';
import { apiService, refreshProfileCache, clearUserCache, safeApiCall, clearDeletedPostCache, saveDeletedPostId } from '../utils/api';
import axios from 'axios';
import HowToVoteOutlinedIcon from '@mui/icons-material/HowToVoteOutlined';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import FlagIcon from '@mui/icons-material/Flag';
import { toast } from 'react-toastify';
import { format, parseISO, isValid } from 'date-fns';
import { ja } from 'date-fns/locale';

// グローバルキャッシュを効率的に初期化
if (typeof window !== 'undefined') {
  // 各キャッシュを安全に初期化
  window.checkedPostsCache = window.checkedPostsCache || new Set();
  window.postsStateCache = window.postsStateCache || new Map();
  window.followStateCache = window.followStateCache || new Map();
  window.batchStatusQueue = window.batchStatusQueue || new Set();
  window.batchPromises = window.batchPromises || new Map();
  window.lastBatchRun = window.lastBatchRun || 0;
  // 投票データがある投稿のIDを記録するためのセットを追加
  window.postsWithPollCache = window.postsWithPollCache || new Set();
  
  // APIが未実装の場合にデモ投票データを表示するフラグ
  window.ENABLE_DEMO_POLLS = false; // ユーザーの要求によりfalseに設定
  
  // API接続先の設定値 - 正しいエンドポイントのみを使用
  window.POLL_API_ENDPOINTS = [
    // GETメソッドのみを使用
    { url: '/api/posts/:id/poll', method: 'GET' }
  ];
  
  // 削除された投稿や無効なIDを記録するセット
  window.INVALID_POST_IDS = window.INVALID_POST_IDS || new Set();
  
  // 開発環境用：明示的に投票を持つと定義する投稿ID配列
  window.POLL_ENABLED_POSTS = window.POLL_ENABLED_POSTS || [11, 12, 13, 7]; // 投票機能を持つ投稿ID
  
  // ユーザープロフィール情報のキャッシュ
  window.userProfileCache = window.userProfileCache || new Map();
}

// 日本時間のタイムゾーン設定
moment.locale('ja');
moment.tz.setDefault('Asia/Tokyo');

// バックエンドのベースURLを設定
const API_BASE_URL = config.API_BASE_URL;

// 画像と動画のURLを正規化するヘルパー関数
export const normalizeImageUrl = (url) => {
  if (!url) return '/images/default-avatar.png';
  
  // すでに完全なURLの場合はそのまま返す
  if (url.startsWith('http') || url.startsWith('data:')) {
    return url;
  }
  
  // "/static/"で始まる相対パスの場合、APIのベースURLと結合
  if (url.startsWith('/static/')) {
    const normalizedUrl = `${config.API_BASE_URL}${url}`;
    return normalizedUrl;
  }
  
  // "static/"で始まる相対パス（先頭スラッシュなし）の場合、APIのベースURLと結合
  if (url.startsWith('static/')) {
    const normalizedUrl = `${config.API_BASE_URL}/${url}`;
    return normalizedUrl;
  }
  
  // /からはじまる場合もAPIのベースURLと結合
  if (url.startsWith('/')) {
    const normalizedUrl = `${config.API_BASE_URL}${url}`;
    return normalizedUrl;
  }
  
  // それ以外の場合はそのまま返す
  return url;
};

import { formatCompleteText } from '../utils/textUtils';

// メンション、ハッシュタグ、絵文字を含むテキストを処理する関数
const formatTextWithMentionsAndHashtags = (text, navigate) => {
  if (!text) return [];
  
  // 新しいテキスト処理関数を使用
  return formatCompleteText(text);
};

// キャッシュのTTL（有効期限）を15秒に短縮（頻繁な更新を確保）
const USER_PROFILE_CACHE_TTL = 15 * 1000; // 15秒

// ユーザープロフィールキャッシュをクリアする関数
const clearUserProfileCache = (username = null) => {
  if (typeof window === 'undefined' || !window.userProfileCache) return;
  
  if (username) {
    // 特定ユーザーのキャッシュのみクリア
    window.userProfileCache.delete(username);
    console.log(`ユーザープロフィールキャッシュクリア: ${username}`);
  } else {
    // 全ユーザーのキャッシュをクリア
    window.userProfileCache.clear();
    console.log('全ユーザープロフィールキャッシュクリア');
  }
};

// ユーザープロフィール情報を取得する関数（キャッシュ機能付き）
const getUserProfile = async (username) => {
  if (!username || typeof window === 'undefined') return null;
  
  // キャッシュをチェック
  const cached = window.userProfileCache.get(username);
  if (cached && (Date.now() - cached.timestamp < USER_PROFILE_CACHE_TTL)) {
    console.log(`ユーザープロフィールキャッシュヒット: ${username}`);
    return cached.data;
  }
  
  try {
    console.log(`ユーザープロフィール取得開始: ${username}`);
    const token = localStorage.getItem('authToken');
    const response = await axios.get(`${config.API_BASE_URL}/api/users/${username}`, {
      headers: token ? {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      } : {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      params: {
        _t: Date.now() // キャッシュ回避
      }
    });
    
    if (response && response.data) {
      // キャッシュに保存する情報を基本プロフィール情報のみに限定
      const basicProfileData = {
        id: response.data.id,
        username: response.data.username,
        display_name: response.data.display_name,
        avatar: response.data.avatar,
        verified: response.data.verified,
        bio: response.data.bio,
        location: response.data.location,
        website: response.data.website,
        is_following: response.data.is_following,
        role: response.data.role,
        // 投稿一覧やフォロワー一覧などの動的データは除外
      };
      
      window.userProfileCache.set(username, {
        data: basicProfileData,
        timestamp: Date.now()
      });
      
      console.log(`ユーザープロフィール新規取得・基本情報のみキャッシュ保存: ${username}`);
      return response.data; // 元の完全データを返す
    }
  } catch (error) {
    console.error(`ユーザープロフィール取得エラー: ${username}`, error);
  }
  
  return null;
};

// 投稿の状態をグローバルキャッシュに保存する関数
const savePostStateToCache = (postId, state) => {
  if (typeof window !== 'undefined' && window.postsStateCache && postId) {
    // キャッシュに新しい状態を保存する前に現在の状態を取得
    const currentState = window.postsStateCache.get(postId) || {};
    // 現在の状態と新しい状態をマージ
    const updatedState = { ...currentState, ...state };
    // マージした状態をキャッシュに保存
    window.postsStateCache.set(postId, updatedState);
  }
};

// 投稿の状態をグローバルキャッシュから取得する関数
const getPostStateFromCache = (postId) => {
  if (typeof window !== 'undefined' && window.postsStateCache && postId) {
    return window.postsStateCache.get(postId);
  }
  return null;
};

// 特定の投稿のキャッシュを強制的にクリアする関数
const clearPostCache = (postId) => {
  if (!postId) return;
  
  try {
    // Post.js内のキャッシュをクリア
    if (typeof window !== 'undefined' && window.postsStateCache) {
      window.postsStateCache.delete(postId);
      console.log(`Post.js: 投稿${postId}のキャッシュを削除`);
    }
    
    // ユーザーアクションキャッシュをクリア
    if (typeof window !== 'undefined' && window.userActionLists) {
      delete window.userActionLists;
      console.log(`Post.js: ユーザーアクションキャッシュを削除`);
    }
    
    // バッチ処理キャッシュをクリア
    if (typeof window !== 'undefined') {
      if (window.batchStatusQueue) {
        window.batchStatusQueue.delete(postId);
      }
      if (window.batchPromises) {
        window.batchPromises.delete(postId);
      }
    }
    
    // 投稿チェック状態もクリア
    try {
      const checkedPosts = JSON.parse(sessionStorage.getItem('checkedPosts') || '{}');
      delete checkedPosts[postId];
      sessionStorage.setItem('checkedPosts', JSON.stringify(checkedPosts));
      console.log(`Post.js: 投稿${postId}のチェック状態を削除`);
    } catch (e) {
      console.warn('Post.js: チェック状態削除エラー:', e);
    }
    
  } catch (error) {
    console.error('Post.js: キャッシュクリアエラー:', error);
  }
};

// デバッグ用：グローバル関数として公開（コンソールから呼び出し可能）
if (typeof window !== 'undefined') {
  window.clearPostCacheDebug = (postId) => {
    console.log(`デバッグ: 投稿${postId}のキャッシュをクリア中...`);
    clearPostCache(postId);
    
    // ページ全体のキャッシュもクリア
    if (window.postsStateCache) {
      console.log(`デバッグ: 全投稿キャッシュサイズ: ${window.postsStateCache.size}`);
    }
    
    // 強制的にページを再レンダリングするためのイベント発火
    window.dispatchEvent(new CustomEvent('cache_cleared', { detail: { postId } }));
    
    console.log(`デバッグ: 投稿${postId}のキャッシュクリア完了`);
  };
  
  window.clearAllPostCaches = () => {
    console.log('デバッグ: 全投稿キャッシュをクリア中...');
    
    if (window.postsStateCache) {
      window.postsStateCache.clear();
      console.log('デバッグ: postsStateCache cleared');
    }
    
    if (window.userActionLists) {
      delete window.userActionLists;
      console.log('デバッグ: userActionLists cleared');
    }
    
    if (window.batchStatusQueue) {
      window.batchStatusQueue.clear();
      console.log('デバッグ: batchStatusQueue cleared');
    }
    
    if (window.batchPromises) {
      window.batchPromises.clear();
      console.log('デバッグ: batchPromises cleared');
    }
    
    try {
      sessionStorage.removeItem('checkedPosts');
      console.log('デバッグ: checkedPosts cleared');
    } catch (e) {
      console.warn('デバッグ: checkedPosts clear error:', e);
    }
    
    console.log('デバッグ: 全投稿キャッシュクリア完了');
  };
}

// 投稿が既にチェック済みかつ有効期限内かを確認する関数
const isPostAlreadyChecked = (postId) => {
  if (!postId) return false;
  
  try {
    const checkedPosts = JSON.parse(sessionStorage.getItem('checkedPosts') || '{}');
    const checkTime = checkedPosts[postId];
    
    if (!checkTime) return false;
    
    // チェック有効期限: 1分間
    const checkExpiryTime = 60 * 1000; // 1分（ミリ秒）
    
    return Date.now() - checkTime < checkExpiryTime;
  } catch (e) {
    
    return false;
  }
};

// リツイート状態の属性を更新する関数
const updateRetweetedAttribute = (id, retweeted) => {
  try {
    const postElement = document.getElementById(`post-${id}`);
    if (postElement) {
      const retweetButton = postElement.querySelector('.post__action--retweet');
      if (retweetButton) {
        retweetButton.setAttribute('data-retweeted', String(retweeted));
        // クラスも更新
        if (retweeted) {
          retweetButton.classList.add('retweeted');
        } else {
          retweetButton.classList.remove('retweeted');
        }
      }
    }
  } catch (error) {
    
  }
};

// バッチ処理で複数投稿のステータスを一度に取得する
// ユーザーアクションリストを取得・キャッシュする関数
const getUserActionLists = async () => {
  // すでにキャッシュがあり、5分以内の場合は再利用
  const cacheKey = 'userActionLists';
  const cached = window[cacheKey];
  if (cached && (Date.now() - cached.timestamp < 300000)) { // 5分間キャッシュ
    console.log('ユーザーアクションリスト: キャッシュから取得');
    return cached.data;
  }
  
  try {
    // トークンを複数の方法で取得を試みる（統一されたキー名を使用）
    let token = localStorage.getItem('authToken');
    
    // 1回目で取得できない場合、少し待ってから再試行
    if (!token) {
      console.log('ユーザーアクションリスト: 初回トークン取得失敗、再試行中...');
      await new Promise(resolve => setTimeout(resolve, 100));
      token = localStorage.getItem('authToken');
    }
    
    // sessionStorageからも試行（統一されたキー名を使用）
    if (!token) {
      token = sessionStorage.getItem('authToken');
      console.log('ユーザーアクションリスト: sessionStorageから取得試行');
    }
    
    // まだ取得できない場合
    if (!token) {
      console.log('ユーザーアクションリスト: トークンなし - localStorage, sessionStorage共に取得失敗');
      console.log('localStorage authToken:', localStorage.getItem('authToken'));
      console.log('sessionStorage authToken:', sessionStorage.getItem('authToken'));
      
      // 念のため古いキー名もチェック
      const legacyToken = localStorage.getItem('token');
      if (legacyToken) {
        console.log('古いキー名でトークン発見 - 新しいキー名に移行');
        localStorage.setItem('authToken', legacyToken);
        localStorage.removeItem('token');
        token = legacyToken;
      } else {
        return { liked_posts: [], bookmarked_posts: [], retweeted_posts: [] };
      }
    }
    
    console.log('ユーザーアクションリスト取得開始:', {
      url: `${config.API_BASE_URL}/api/posts/user-actions`,
      hasToken: !!token,
      tokenStart: token.substring(0, 20) + '...'
    });
    
    const response = await axios.get(`${config.API_BASE_URL}/api/posts/user-actions`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    console.log('ユーザーアクションリスト取得レスポンス:', {
      status: response.status,
      data: response.data
    });
    
    const data = response.data || { liked_posts: [], bookmarked_posts: [], retweeted_posts: [] };
    
    // キャッシュに保存
    window[cacheKey] = {
      data: data,
      timestamp: Date.now()
    };
    
    console.log('ユーザーアクションリスト取得成功:', {
      liked: data.liked_posts?.length || 0,
      bookmarked: data.bookmarked_posts?.length || 0,
      retweeted: data.retweeted_posts?.length || 0,
      user_id: data.user_id
    });
    
    return data;
  } catch (error) {
    console.error('ユーザーアクションリスト取得エラー:', {
      error: error.message,
      status: error.response?.status,
      data: error.response?.data,
      url: `${config.API_BASE_URL}/api/posts/user-actions`
    });
    return { liked_posts: [], bookmarked_posts: [], retweeted_posts: [] };
  }
};

const getBatchedPostStatus = async (postId) => {
  // グローバル変数の初期化
  if (!window.batchStatusQueue) window.batchStatusQueue = new Set();
  if (!window.batchPromises) window.batchPromises = new Map();
  
  // 数値型のIDでない場合はデフォルト値を返す
  if (isNaN(parseInt(postId)) || String(postId).includes('reply_')) {
    
    return {
      liked: false,
      bookmarked: false,
      retweeted: false,
      like_count: 0,
      retweet_count: 0,
      comment_count: 0
    };
  }
  
  // キューにこの投稿IDを追加
  window.batchStatusQueue.add(postId);
  
  // 既にこのIDに対するPromiseが存在する場合はそれを返す
  if (window.batchPromises.has(postId)) {
    return window.batchPromises.get(postId).promise;
  }
  
  // 新しいPromiseを作成
  let resolveFunction;
  const promise = new Promise((resolve) => {
    resolveFunction = resolve;  // resolveを保存
    
    // 100ms後に実行
    setTimeout(async () => {
      try {
        // 新しいアプローチ：ユーザーアクションリストから判定
        const userActions = await getUserActionLists();
        const postIdStr = String(postId);
        
        // 基本的なカウント情報も取得（個別エンドポイント使用）
        let token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        const response = await axios.get(`${config.API_BASE_URL}/api/posts/${postId}/status`, {
          headers: token ? {
            'Authorization': `Bearer ${token}`
          } : {}
        });
        
        const apiData = response.data || {};
        
        // ユーザーアクションリストから状態を判定
        const data = {
          liked: userActions.liked_posts?.includes(postIdStr) || false,
          is_liked: userActions.liked_posts?.includes(postIdStr) || false,
          bookmarked: userActions.bookmarked_posts?.includes(postIdStr) || false,
          is_bookmarked: userActions.bookmarked_posts?.includes(postIdStr) || false,
          retweeted: userActions.retweeted_posts?.includes(postIdStr) || false,
          is_retweeted: userActions.retweeted_posts?.includes(postIdStr) || false,
          like_count: apiData.like_count || 0,
          retweet_count: apiData.retweet_count || 0,
          comment_count: apiData.comment_count || 0
        };
        
        console.log(`投稿${postId}のステータス判定:`, {
          liked: data.liked,
          bookmarked: data.bookmarked,
          retweeted: data.retweeted,
          counts: {
            likes: data.like_count,
            retweets: data.retweet_count,
            comments: data.comment_count
          }
        });
        
        resolve(data);
        
        // 使用済みのPromiseをマップから削除
        window.batchPromises.delete(postId);
        
      } catch (error) {
        console.error('投稿ステータス取得エラー:', error);
        resolve(null);
        window.batchPromises.delete(postId);
      } finally {
        // キューから削除
        window.batchStatusQueue.delete(postId);
      }
    }, 100);
  });
  
  // Promiseと解決関数をマップに保存
  window.batchPromises.set(postId, {
    promise: promise,
    resolve: resolveFunction
  });
  
  return promise;
};

// ユーザーアクションキャッシュを更新する関数
const updateUserActionCache = (postId, action, isAdded) => {
  const cacheKey = 'userActionLists';
  const cached = window[cacheKey];
  if (!cached || !cached.data) return;
  
  const postIdStr = String(postId);
  const actionKey = `${action}_posts`; // liked_posts, bookmarked_posts, retweeted_posts
  
  if (isAdded) {
    // アクションを追加
    if (!cached.data[actionKey].includes(postIdStr)) {
      cached.data[actionKey].push(postIdStr);
    }
  } else {
    // アクションを削除
    const index = cached.data[actionKey].indexOf(postIdStr);
    if (index > -1) {
      cached.data[actionKey].splice(index, 1);
    }
  }
  
  console.log(`キャッシュ更新: ${action} ${isAdded ? '追加' : '削除'} - 投稿${postId}`);
};

// 投票データがある投稿かどうか確認する関数
const isPostWithPoll = (postId) => {
  if (typeof window !== 'undefined' && postId) {
    const numericId = parseInt(postId);
    
    // メモリキャッシュが初期化されていない場合は初期化
    if (!window.postsWithPollCache) {
      window.postsWithPollCache = new Set();
    }
    
    // すでにキャッシュに含まれている場合は再チェック不要
    if (window.postsWithPollCache.has(numericId)) {
      return true;
    }
    
    // 1. 明示的に定義された投票機能付き投稿IDリストを確認
    if (window.POLL_ENABLED_POSTS && Array.isArray(window.POLL_ENABLED_POSTS)) {
      if (window.POLL_ENABLED_POSTS.includes(numericId)) {
        // 開発環境のみログ出力（本番環境では出力しない）
        if (process.env.NODE_ENV === 'development') {
          
        }
        // キャッシュに追加して次回以降のチェックを省略
        window.postsWithPollCache.add(numericId);
        return true;
      }
    }
    
    // 3. ローカルストレージを確認
    try {
      const hasPoll = localStorage.getItem(`post_${numericId}_has_poll`) === 'true';
      if (hasPoll) {
        // メモリキャッシュに追加
        window.postsWithPollCache.add(numericId);
        return true;
      }
    } catch (e) {
      // ローカルストレージエラー処理
    }
  }
  
  // どの条件にも合致しない場合は投票データなしと判断
  return false;
};

function Post({
  id,
  displayName,
  username,
  verified,
  text,
  avatar,
  image,
  video, // 動画プロパティを追加
  createdAt,
  likes,
  comments,
  retweets,
  isRetweet,
  retweeter,
  retweeted: initialRetweeted,
  authorId,
  viewOnly,
  onLike,
  onRetweet,
  onComment,
  onDelete,
  onSave,
  onRemoveSave,
  isDetailPage = false,
  isPinned = false,
  isProfilePage = false,
  poll = null,
  has_voted = false,
  has_poll = false, // 投票機能を持つかどうかを示すフラグ
  postLocation = null, // 位置情報プロパティを追加（locationからpostLocationへ変更）
  shifts = null, // シフト情報を追加
  adMode = false, // 広告モードフラグ
  hideCommentIcon = false, // コメントアイコン非表示フラグ
  hideRetweetIcon = false, // リツイートアイコン非表示フラグ
  userActionsLoaded = false, // Home.jsでユーザーアクションが既に読み込み済みかどうか
  liked: initialLiked = false, // Home.jsから渡されるいいね状態
  bookmarked: initialBookmarked = false // Home.jsから渡されるブックマーク状態
}) {
  // ステートのキャッシュから初期値を取得
  const cachedState = getPostStateFromCache(id);
  

  
  // 投票データがある投稿かどうかを確認
  const hasPostPoll = useMemo(() => {
    return has_poll || isPostWithPoll(id) || (cachedState && cachedState.pollData);
  }, [has_poll, id, cachedState]);
  
  const [postData, setPostData] = useState({});
  const [liked, setLiked] = useState(() => {
    // キャッシュから取得、なければpropsの値を使用、最終的にはfalse
    return cachedState?.liked !== undefined ? cachedState.liked : initialLiked;
  });
  const [likeCount, setLikeCount] = useState(cachedState ? cachedState.likeCount : (likes || 0));
  const [retweeted, setRetweeted] = useState(() => {
    // キャッシュから取得、なければpropsの値を使用、最終的にはfalse
    return cachedState?.retweeted !== undefined ? cachedState.retweeted : (initialRetweeted || false);
  });
  const [retweetCount, setRetweetCount] = useState(cachedState ? cachedState.retweetCount : (retweets || 0));
  const [bookmarked, setBookmarked] = useState(() => {
    // キャッシュから取得、なければpropsの値を使用、最終的にはfalse
    return cachedState?.bookmarked !== undefined ? cachedState.bookmarked : initialBookmarked;
  });
  const [showCommentField, setShowCommentField] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentsList, setCommentsList] = useState(cachedState ? cachedState.commentsList : []);
  const [commentsCount, setCommentsCount] = useState(cachedState ? cachedState.commentsCount : (comments || 0));
  const [loadingComments, setLoadingComments] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(cachedState ? cachedState.isDeleted : false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [profileCardPosition, setProfileCardPosition] = useState({ top: 0, left: 0 });
  const [profileData, setProfileData] = useState(null);
  const [isFollowing, setIsFollowing] = useState(cachedState ? cachedState.isFollowing : false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [hideCardTimeoutId, setHideCardTimeoutId] = useState(null);
  const [isPinnedPost, setIsPinnedPost] = useState(cachedState ? cachedState.isPinnedPost : isPinned);
  const [isPinning, setIsPinning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, currentUser, token, checkPermission } = useAuth();
  const avatarRef = useRef(null);
  const videoRef = useRef(null); // 動画要素への参照
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [pollData, setPollData] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [voteLoading, setVoteLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [postText, setPostText] = useState(text || ''); // 投稿テキストをステートで明示的に管理
  
  // コメント表示と操作に関する状態
  const [showComments, setShowComments] = useState(false); // コメント表示制御
  const [commentLikes, setCommentLikes] = useState({}); // コメントいいねの状態
  
  // 返信機能のための状態を追加
  const [replyToCommentId, setReplyToCommentId] = useState(null); // 返信対象のコメントID
  const [replyText, setReplyText] = useState(''); // 返信テキスト
  const [isSubmittingReply, setIsSubmittingReply] = useState(false); // 返信送信中フラグ
  const [replyError, setReplyError] = useState(''); // 返信エラーメッセージ
  const [commentReplies, setCommentReplies] = useState({}); // コメントごとの返信リスト
  const [showReplies, setShowReplies] = useState({}); // 返信表示状態
  
  // ユーザー情報のステート追加
  const [postAuthor, setPostAuthor] = useState(cachedState ? cachedState.postAuthor : {
    displayName: (isLoggedIn && currentUser && currentUser.id === authorId) 
      ? (currentUser.display_name || currentUser.displayName || displayName || 'ユーザー') 
      : (displayName || 'ユーザー'),
    username: (isLoggedIn && currentUser && currentUser.id === authorId)
      ? (currentUser.username || username || '不明')
      : (username || '不明'),
    avatar: (isLoggedIn && currentUser && currentUser.id === authorId)
      ? (currentUser.avatar || avatar || '')
      : (avatar || ''),
    verified: (isLoggedIn && currentUser && currentUser.id === authorId)
      ? (currentUser.verified || verified || false)
      : (verified || false)
  });
  
  // リツイート情報のステートを追加
  const [retweetInfo, setRetweetInfo] = useState(cachedState ? cachedState.retweetInfo : {
    isRetweet: isRetweet || false,
    retweeter: retweeter || null
  });
  
  // 権限チェック
  const canLike = isLoggedIn && checkPermission('LIKE');
  const canRetweet = isLoggedIn && checkPermission('RETWEET');
  
  // ローディング状態の管理
  const [likeLoading, setLikeLoading] = useState(false);
  const [retweetLoading, setRetweetLoading] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  
  // ユーザーロール判定
  const isGuest = !isLoggedIn || !currentUser;
  const isViewer = isLoggedIn && currentUser?.role === 'viewer';
  const isGuestOrViewer = isGuest || isViewer;
  
  // ゲストユーザー向けのポップアップ表示
  const showGuestWarning = () => {
    alert('会員登録が必要です');
  };
  
  const canDeletePost = isLoggedIn && currentUser && (
    currentUser.id === authorId ||
    currentUser.role === 'admin' ||
    currentUser.role === 'moderator'
  );

  // 投稿所有者かどうかのチェック
  const isPostOwner = isLoggedIn && currentUser && currentUser.id === authorId;

  // ユーザーアクションリストを取得・キャッシュする関数（コンポーネント内版）
  const getUserActionLists = useCallback(async () => {
    // すでにキャッシュがあり、5分以内の場合は再利用
    const cacheKey = 'userActionLists';
    const cached = window[cacheKey];
    if (cached && (Date.now() - cached.timestamp < 300000)) { // 5分間キャッシュ
      console.log('ユーザーアクションリスト: キャッシュから取得');
      return cached.data;
    }
    
    try {
      // ログイン状態を確認
      if (!isLoggedIn || !currentUser) {
        console.log('ユーザーアクションリスト: 未ログイン状態');
        return { liked_posts: [], bookmarked_posts: [], retweeted_posts: [] };
      }
      
      // トークンを複数の方法で取得を試みる（統一されたキー名を使用）
      let token = localStorage.getItem('authToken');
      
      // 1回目で取得できない場合、少し待ってから再試行
      if (!token) {
        console.log('ユーザーアクションリスト: 初回トークン取得失敗、再試行中...');
        await new Promise(resolve => setTimeout(resolve, 100));
        token = localStorage.getItem('authToken');
      }
      
      // sessionStorageからも試行（統一されたキー名を使用）
      if (!token) {
        token = sessionStorage.getItem('authToken');
        console.log('ユーザーアクションリスト: sessionStorageから取得試行');
      }
      
      // まだ取得できない場合
      if (!token) {
        console.log('ユーザーアクションリスト: トークンなし - localStorage, sessionStorage共に取得失敗');
        console.log('localStorage authToken:', localStorage.getItem('authToken'));
        console.log('sessionStorage authToken:', sessionStorage.getItem('authToken'));
        
        // 念のため古いキー名もチェック
        const legacyToken = localStorage.getItem('token');
        if (legacyToken) {
          console.log('古いキー名でトークン発見 - 新しいキー名に移行');
          localStorage.setItem('authToken', legacyToken);
          localStorage.removeItem('token');
          token = legacyToken;
        } else {
          console.log('isLoggedIn:', isLoggedIn, 'currentUser:', currentUser);
          return { liked_posts: [], bookmarked_posts: [], retweeted_posts: [] };
        }
      }
      
      console.log('ユーザーアクションリスト取得開始:', {
        url: `${config.API_BASE_URL}/api/posts/user-actions`,
        hasToken: !!token,
        tokenStart: token.substring(0, 20) + '...',
        isLoggedIn: isLoggedIn,
        userId: currentUser?.id
      });
      
      const response = await axios.get(`${config.API_BASE_URL}/api/posts/user-actions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('ユーザーアクションリスト取得レスポンス:', {
        status: response.status,
        data: response.data
      });
      
      const data = response.data || { liked_posts: [], bookmarked_posts: [], retweeted_posts: [] };
      
      // キャッシュに保存
      window[cacheKey] = {
        data: data,
        timestamp: Date.now()
      };
      
      console.log('ユーザーアクションリスト取得成功:', {
        liked: data.liked_posts?.length || 0,
        bookmarked: data.bookmarked_posts?.length || 0,
        retweeted: data.retweeted_posts?.length || 0,
        user_id: data.user_id
      });
      
      return data;
    } catch (error) {
      console.error('ユーザーアクションリスト取得エラー:', {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: `${config.API_BASE_URL}/api/posts/user-actions`
      });
      return { liked_posts: [], bookmarked_posts: [], retweeted_posts: [] };
    }
  }, [isLoggedIn, currentUser]);

  // 投票処理の関数
  const handleVote = async (optionId) => {
    if (!isLoggedIn) {
      // ログインしていない場合はログイン画面へ
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    // 投票中の場合は処理しない
    if (voteLoading) {
      return;
    }
    
    // 現在の投票データを取得（pollDataまたはpollから）
    let currentPollData = pollData || poll;
    
    // 投票データが完全にない場合は、最小限のデータを作成
    if (!currentPollData) {
      
      try {
        // optionIdからオプションを推測
        const numericOptionId = parseInt(optionId);
        currentPollData = {
          options: [
            { id: numericOptionId, text: `選択肢 ${numericOptionId}`, votes: 0 }
          ],
          total_votes: 0,
          is_closed: false,
          end_date: moment().add(1, 'day').toISOString()
        };
      } catch (e) {
        
        toast.error('投票データが不正です');
        return;
      }
    }
    
    if (currentPollData && currentPollData.is_closed) {
      toast.info('この投票は終了しています');
      return;
    }

    setVoteLoading(true);
    try {
      // トークンを取得（AuthContextと同じキーを使用）
      const token = localStorage.getItem('authToken');
      
      
      if (!token) {
        
        setVoteLoading(false);
        navigate('/login', { state: { from: location.pathname } });
        return;
      }
      
      // idを検証（文字列のまま使用 - v2ではUUID形式のID）
      const postIdStr = String(id);
      if (!postIdStr || postIdStr === 'undefined' || postIdStr === 'null') {
        
        toast.error('無効な投稿IDです');
        setVoteLoading(false);
        return;
      }
      
      // オプションIDも検証
      const numericOptionId = parseInt(optionId);
      if (isNaN(numericOptionId)) {
        
        toast.error('無効な選択肢です');
        setVoteLoading(false);
        return;
      }
      
      // 複数のAPIエンドポイントを試す
      const voteEndpoints = [
        `/api/posts/${postIdStr}/vote/${numericOptionId}`,    // 標準形式
        `/api/posts/${postIdStr}/polls/vote/${numericOptionId}`, // 代替形式1
        `/api/polls/${postIdStr}/options/${numericOptionId}/vote`, // 代替形式2
        `/api/poll/${postIdStr}/vote?option=${numericOptionId}`  // クエリパラメータ形式
      ];
      
      let responseData = null;
      let lastError = null;
      
      // 各エンドポイントを順に試す
      for (const endpoint of voteEndpoints) {
        try {
          
          
          // APIリクエストを送信
          const response = await axios.post(`${config.API_BASE_URL}${endpoint}`, {}, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });
          
          
          responseData = response.data;
          break; // 成功したらループを抜ける
        } catch (err) {
          
          lastError = err;
          
          // 405エラーの場合は別のエンドポイントを試す
          if (err.response && err.response.status === 405) {
            continue;
          }
          
          // 401エラー（認証エラー）の場合はすぐに失敗
          if (err.response && err.response.status === 401) {
            throw err;
          }
        }
      }
      
      // いずれかのAPIが成功した場合
      if (responseData) {
        // APIレスポンスからオプションを取得
        let updatedOptions = responseData.options;
        
        // APIレスポンスにoptionsがない場合、現在のオプションを更新
        if (!updatedOptions && currentPollData.options) {
          
          updatedOptions = [...currentPollData.options];
          
          // クリックされたオプションの投票数を1増やす
          const optionIndex = updatedOptions.findIndex(opt => opt.id === numericOptionId);
          if (optionIndex !== -1) {
            updatedOptions[optionIndex] = {
              ...updatedOptions[optionIndex],
              votes: (updatedOptions[optionIndex].votes || 0) + 1
            };
          }
        }
        
        // 投票データを更新
        const updatedPollData = {
          ...currentPollData,
          options: updatedOptions || currentPollData.options,
          total_votes: responseData.total_votes || (currentPollData.total_votes + 1),
          user_vote: responseData.user_vote || numericOptionId
        };
        
        
        setPollData(updatedPollData);
        setHasVoted(true);
        
        // 更新された投票データをキャッシュに保存
        savePostStateToCache(id, {
          pollData: updatedPollData,
          hasVoted: true
        });
        
        // 投票データがある投稿としてマーク
        markPostWithPoll(postIdStr);
        
        // 成功メッセージを表示
        toast.success('投票しました');
      } else if (lastError) {
        // すべてのAPIが失敗した場合
        throw lastError;
      } else {
        // 謎のエラー
        throw new Error('投票に失敗しました。APIからの応答がありません。');
      }
    } catch (error) {
      
      
      // エラーレスポンスの詳細をログ出力
      if (error.response) {
        
      }
      
      // エラーメッセージを表示
      toast.error('投票に失敗しました: ' + (error.response?.data?.message || error.message || 'APIエラー'));
      
      // API未実装かもしれないのでオプティミスティックな更新を試みる
      try {
        if (currentPollData && currentPollData.options) {
          
          const updatedOptions = [...currentPollData.options];
          
          // クリックされたオプションの投票数を1増やす
          const optionIndex = updatedOptions.findIndex(opt => opt.id === parseInt(optionId));
          if (optionIndex !== -1) {
            updatedOptions[optionIndex] = {
              ...updatedOptions[optionIndex],
              votes: (updatedOptions[optionIndex].votes || 0) + 1
            };
          }
          
          // 投票データを更新
          const updatedPollData = {
            ...currentPollData,
            options: updatedOptions,
            total_votes: (currentPollData.total_votes || 0) + 1,
            user_vote: parseInt(optionId)
          };
          
          
          setPollData(updatedPollData);
          setHasVoted(true);
          
          // 更新された投票データをキャッシュに保存
          savePostStateToCache(id, {
            pollData: updatedPollData,
            hasVoted: true
          });
        }
      } catch (optError) {
        
      }
      
      // 認証エラーの場合はログイン画面へリダイレクト
      if (error.response && error.response.status === 401) {
        navigate('/login', { state: { from: location.pathname } });
      }
    } finally {
      setVoteLoading(false);
    }
  };

  // 投票取り消しの関数を改善
  const handleCancelVote = async () => {
    if (!isLoggedIn) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    
    
    // 投票中または投票データがない場合は処理しない
    if (voteLoading) {
      
      return;
    }
    
    // 現在の投票データを取得
    const currentPollData = pollData || poll;
    if (!currentPollData) {
      
      toast.error('投票データが見つかりません');
      return;
    }
    
    if (currentPollData.is_closed) {
      
      toast.info('この投票は終了しているため、取り消しできません');
      return;
    }
    
    // 投票していない場合は何もしない
    if (!hasVoted && !currentPollData.user_vote) {
      
      toast.info('投票していないため、取り消しできません');
      return;
    }

    setVoteLoading(true);
    
    // オプティミスティックUIアップデートのために現在の状態を保存
    const originalPollData = { ...currentPollData };
    const originalHasVoted = hasVoted;
    
    try {
      // 事前にUIを更新（オプティミスティックアップデート）
      const userVote = currentPollData.user_vote;
      const updatedOptions = [...currentPollData.options];
      
      // 投票したオプションを見つけて投票数を減らす
      const votedOptionIndex = updatedOptions.findIndex(opt => opt.id === userVote);
      if (votedOptionIndex !== -1 && updatedOptions[votedOptionIndex].votes > 0) {
        updatedOptions[votedOptionIndex] = {
          ...updatedOptions[votedOptionIndex],
          votes: updatedOptions[votedOptionIndex].votes - 1
        };
      }
      
      // 投票データを更新
      const optimisticPollData = {
        ...currentPollData,
        options: updatedOptions,
        total_votes: Math.max(0, (currentPollData.total_votes || 0) - 1),
        user_vote: null
      };
      
      // 即時UIに反映
      setPollData(optimisticPollData);
      setHasVoted(false);
      
      // APIリクエストを試行
      let successResponse = null;
      let requestError = null;
      
      // トークンを取得（AuthContextと同じキーを使用）
      const token = localStorage.getItem('authToken');
      
      
      if (!token) {
        throw new Error('認証トークンがありません');
      }
      
      // idを数値に変換
      const numericPostId = parseInt(id);
      if (isNaN(numericPostId)) {
        throw new Error('無効な投稿IDです');
      }
      
      // 複数のAPIエンドポイントを試す
      const apiEndpoints = [
        `/api/posts/${numericPostId}/vote`,      // 標準形式
        `/api/posts/${numericPostId}/polls/vote`, // 代替形式1
        `/api/polls/${numericPostId}/vote`,       // 代替形式2
        `/api/vote/${numericPostId}`              // 代替形式3
      ];
      
      // 各エンドポイントを順番に試す
      for (const endpoint of apiEndpoints) {
        try {
          
          
          // 投票を取り消すDELETEリクエスト
          const response = await axios.delete(`${config.API_BASE_URL}${endpoint}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          
          successResponse = response;
          break; // 成功したらループを抜ける
        } catch (err) {
          
          requestError = err;
          
          // 404エラーは続行（別のエンドポイントを試す）
          if (err.response && err.response.status === 404) {
            continue;
          }
          
          // 401エラー（認証エラー）はすぐに失敗
          if (err.response && err.response.status === 401) {
            throw err;
          }
        }
      }
      
      // いずれかのAPIが成功した場合
      if (successResponse && successResponse.data) {
        
        
        // APIから返された最新データを使用
        const updatedPollData = {
          ...currentPollData,
          options: successResponse.data.options || optimisticPollData.options,
          total_votes: successResponse.data.total_votes || optimisticPollData.total_votes,
          user_vote: null
        };
        
        
        setPollData(updatedPollData);
        setHasVoted(false);
        
        // 更新された投票データをキャッシュに保存
        savePostStateToCache(id, {
          pollData: updatedPollData,
          hasVoted: false
        });
        
        // 成功メッセージを表示
        toast.success('投票を取り消しました');
      } 
      // すべてのAPIが失敗した場合でも、フロントエンドの表示は更新済み
      else if (requestError) {
        
        
        
        // キャッシュに保存（サーバー側が失敗してもUIは更新）
        savePostStateToCache(id, {
          pollData: optimisticPollData,
          hasVoted: false
        });
        
        // エラーだがUIは更新したことを通知
        toast.warning('サーバーエラーが発生しましたが、投票は取り消されました。ページを再読み込みすると元に戻る場合があります。');
      }
    } catch (error) {
      
      
      // エラーレスポンスの詳細をログ出力
      if (error.response) {
        
      }
      
      // 元の状態に戻す
      setPollData(originalPollData);
      setHasVoted(originalHasVoted);
      
      // エラーメッセージを表示
      toast.error('投票の取り消しに失敗しました: ' + (error.response?.data?.message || error.message || 'APIエラー'));
      
      // 認証エラーの場合はログイン画面へリダイレクト
      if (error.response && error.response.status === 401) {
        navigate('/login', { state: { from: location.pathname } });
      }
    } finally {
      setVoteLoading(false);
    }
  };

  // 投票パーセンテージの計算を改善
  const calculatePercentage = (votes, total) => {
    // 投票数と合計が数値かどうかを検証
    const numericVotes = typeof votes === 'number' ? votes : parseInt(votes) || 0;
    const numericTotal = typeof total === 'number' ? total : parseInt(total) || 0;
    
    // 合計が0または無効な場合は0を返す
    if (!numericTotal || numericTotal <= 0) return 0;
    
    // パーセンテージを計算して四捨五入
    return Math.round((numericVotes / numericTotal) * 100);
  };

  // テキストの変更を監視して状態を更新
  useEffect(() => {
    setPostText(text || '');
    
  }, [text]);

  // videoプロパティの変更を監視してデバッグ
  useEffect(() => {
    
    
    
    // videoRefの存在確認
    if (videoRef.current) {
      
    }
  }, [video, id]);

  // コメント取得関数
  const fetchComments = async () => {
    if (loadingComments) return;
    
    try {
      setLoadingComments(true);
      
      // キャッシュ回避のためのタイムスタンプパラメータを追加
      const response = await apiService.get(`/api/posts/${id}/comments?_t=${Date.now()}`);
      if (response && response.data && response.data.comments) {
        // セッションストレージから非表示コメントのID一覧を取得
        let hiddenCommentsIds = [];
        try {
          hiddenCommentsIds = JSON.parse(sessionStorage.getItem(`hiddenComments_${id}`) || '[]');
        } catch (storageError) {
          
        }
        
        // 非表示コメントを除外したコメントリストを作成
        const filteredComments = hiddenCommentsIds.length > 0 
          ? response.data.comments.filter(comment => !hiddenCommentsIds.includes(comment.id))
          : response.data.comments;
        
        // コメント数を調整（APIから返されたトータル数から非表示分を引く）
        const adjustedCount = Math.max(0, 
          (response.data.total || response.data.comments.length) - hiddenCommentsIds.length
        );
        
        setCommentsList(filteredComments);
        setCommentsCount(adjustedCount);
        
        // コメントのいいね状態を設定
        const commentLikesState = {};
        filteredComments.forEach(comment => {
          if (comment.id && comment.hasOwnProperty('liked')) {
            commentLikesState[comment.id] = comment.liked;
          }
        });
        setCommentLikes(prevLikes => ({
          ...prevLikes,
          ...commentLikesState
        }));
        
        // キャッシュに保存
        savePostStateToCache(id, { 
          commentsList: filteredComments,
          commentsCount: adjustedCount,
          commentLikes: commentLikesState
        });
      }
    } catch (error) {
      
    } finally {
      setLoadingComments(false);
    }
  };

  // コメント表示切替関数
  const toggleComments = () => {
    // コメントの表示/非表示を切り替え
    const newShowComments = !showComments;
    setShowComments(newShowComments);
    
    // 表示する場合は常にコメントを読み込む
    if (newShowComments) {
      fetchComments();
    }
  };

  // isDetailPage の場合、マウント時にコメントを自動展開
  useEffect(() => {
    if (isDetailPage && id) {
      setShowComments(true);
      fetchComments();
    }
  }, [isDetailPage, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 返信モードを切り替える関数
  const toggleReplyMode = (commentId) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    // 対象コメントを取得
    const targetComment = commentsList.find(c => c.id === commentId);
    
    // 同じコメントへの返信モードを切り替え、別のコメントの場合は切り替え
    if (replyToCommentId === commentId) {
      // 既に選択されているコメントの場合はモードを解除
      setReplyToCommentId(null);
      setReplyText('');
    } else {
      // 新しいコメントへの返信の場合
      setReplyToCommentId(commentId);
      
      // ユーザー名が存在する場合は自動的にメンションを追加
      if (targetComment && targetComment.user && targetComment.user.username) {
        setReplyText(`@${targetComment.user.username} `);
      } else {
        setReplyText('');
      }
    }
    
    setReplyError('');
  };

  // 返信を送信する関数
  const handlePostReply = async (commentId) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    if (!replyText.trim()) {
      setReplyError('返信内容を入力してください');
      return;
    }
    
    // 対象コメントを取得
    const targetComment = commentsList.find(c => c.id === commentId);
    
    // メンションを確認・追加
    let finalReplyText = replyText.trim();
    
    // ユーザー名が存在し、メンションが含まれていない場合は先頭に追加
    if (targetComment && targetComment.user && targetComment.user.username) {
      const mentionText = `@${targetComment.user.username}`;
      if (!finalReplyText.includes(mentionText)) {
        finalReplyText = `${mentionText} ${finalReplyText}`;
      }
    }
    
    try {
      setIsSubmittingReply(true);
      
      // デバッグ用ログ追加
      
      
      // リクエスト形式を修正 - 実際のバックエンドAPIに合わせる
      // 1. 直接axiosを使用してトークンを明示的に指定（正しいキーを使用）
      const token = localStorage.getItem('authToken');
      let response;
      
      try {
        // バックエンドの実際のエンドポイント形式に合わせる (複数のエンドポイント形式を試す)
        // 方法1: コメントへの返信として
        response = await axios.post(`${config.API_BASE_URL}/api/posts/${id}/comments/${commentId}/replies`, 
          { text: finalReplyText },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } catch (apiErr1) {
        
        try {
          // 方法2: 通常のコメント追加として (parent_id パラメータ付き)
          response = await axios.post(`${config.API_BASE_URL}/api/posts/${id}/comments`, 
            { text: finalReplyText, parent_id: commentId },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
        } catch (apiErr2) {
          
          try {
            // 方法3: コメントID付きのエンドポイント
            response = await axios.post(`${config.API_BASE_URL}/api/comments/${commentId}/replies`, 
              { text: finalReplyText, post_id: id },
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
          } catch (apiErr3) {
            
            // 全てのAPIエンドポイントが失敗した場合、最後のエラーをスローする
            throw apiErr3;
          }
        }
      }
      
      // 応答データのデバッグ
      
      
      if (response && response.data) {
        // 応答データの構造を確認
        let newReply = response.data;
        
        // レスポンス形式の調整 (APIによって形式が異なる場合)
        const formattedReply = {
          // 複数の可能性のあるレスポンス構造に対応
          id: newReply.reply?.id || newReply.id || newReply.comment?.id || Date.now(),
          text: newReply.reply?.text || newReply.text || newReply.comment?.text || finalReplyText,
          user: newReply.reply?.user || newReply.user || currentUser,
          created_at: newReply.reply?.created_at || newReply.created_at || newReply.comment?.created_at || new Date().toISOString(),
          parent_id: commentId
        };
        
        
        
        // 返信データが確実にユーザー情報を持っていることを確認
        if (!formattedReply.user) {
          formattedReply.user = currentUser;
        }
        
        // 返信IDを数値型にしない（文字列として保持）
        if (typeof formattedReply.id === 'number') {
          formattedReply.id = String(formattedReply.id);
        }
        
        // コメントIDも文字列として扱う
        const commentIdStr = typeof commentId === 'number' ? String(commentId) : commentId;
        
        // コメントの返信リストを更新
        setCommentReplies(prevReplies => ({
          ...prevReplies,
          [commentIdStr]: [...(prevReplies[commentIdStr] || []), formattedReply]
        }));
        
        // 返信表示状態を更新（返信後は表示する）
        setShowReplies(prevState => ({
          ...prevState,
          [commentIdStr]: true
        }));
        
        // フォームをリセット
        setReplyText('');
        setReplyToCommentId(null);
        setReplyError('');
        
        // キャッシュに保存
        savePostStateToCache(id, { 
          commentReplies: {
            ...commentReplies,
            [commentIdStr]: [...(commentReplies[commentIdStr] || []), formattedReply]
          },
          showReplies: {
            ...showReplies,
            [commentIdStr]: true
          }
        });
        
        // 成功メッセージを表示
        toast.success('返信を投稿しました');
        
        // 成功メッセージをログに出力
        
      }
    } catch (error) {
      
      
      // エラー詳細をログ出力
      if (error.response) {
        
      }
      
      setReplyError('返信の投稿に失敗しました。API未実装の可能性があります。');
      
      if (error.response && error.response.status === 401) {
        navigate('/login');
      }
    } finally {
      setIsSubmittingReply(false);
    }
  };
  
  // 返信を削除する関数
  const handleDeleteReply = async (commentId, replyId) => {
    if (!window.confirm('この返信を削除しますか？')) {
      return;
    }

    try {
      // トークンを取得（正しいキーを使用）
      const token = localStorage.getItem('authToken');
      
      // APIを呼び出してリプライを削除（3つの異なるエンドポイントを試す）
      let response = null;
      let responseSuccess = false;
      
      try {
        // 方法1: コメントへの返信として
        response = await axios.delete(`${config.API_BASE_URL}/api/comments/${commentId}/replies/${replyId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        responseSuccess = true;
      } catch (apiErr1) {
        
        
        try {
          // 方法2: 別のエンドポイント形式
          response = await axios.delete(`${config.API_BASE_URL}api/comments/${replyId}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          responseSuccess = true;
        } catch (apiErr2) {
          
          
          try {
            // 方法3: apiServiceを使用
            response = await apiService.delete(`/api/comments/${commentId}/replies/${replyId}`);
            responseSuccess = true;
          } catch (apiErr3) {
            if (apiErr3.response && apiErr3.response.status === 404) {
              
              responseSuccess = true; // UIから削除するため成功とみなす
            } else {
              
              // 全てのAPIエンドポイントが失敗した場合、最後のエラーをスローする
              throw apiErr3;
            }
          }
        }
      }
      
      // UIから返信を削除
      setCommentReplies(prevReplies => ({
        ...prevReplies,
        [commentId]: (prevReplies[commentId] || []).filter(reply => reply.id !== replyId)
      }));
      
      // キャッシュを更新
      savePostStateToCache(id, { 
        commentReplies: {
          ...commentReplies,
          [commentId]: (commentReplies[commentId] || []).filter(reply => reply.id !== replyId)
        }
      });
      
      // 成功メッセージ
      toast.success(response?.data?.message || '返信を削除しました');
    } catch (error) {
      
      
      // エラー詳細をログ出力
      if (error.response) {
        
      }
      
      // エラーメッセージを表示
      let errorMessage = '返信の削除に失敗しました';
      if (error.response?.data?.error) {
        errorMessage += `: ${error.response.data.error}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      toast.error(errorMessage);
      
      // 認証エラーの場合はログイン画面へ
      if (error.response && error.response.status === 401) {
        navigate('/login');
      }
    }
  };
  
  // 返信を表示/非表示にする関数
  const toggleReplies = async (commentId) => {
    // 既に返信データがある場合は表示/非表示を切り替えるだけ
    const hasReplies = commentReplies[commentId] && commentReplies[commentId].length > 0;
    
    // 現在の表示状態を反転
    const newShowReplies = !showReplies[commentId];
    
    // 表示状態を更新
    setShowReplies(prevState => ({
      ...prevState,
      [commentId]: newShowReplies
    }));
    
    // 返信データがなく、表示する場合は取得
    if (!hasReplies && newShowReplies) {
      try {
        const response = await apiService.get(`/api/comments/${commentId}/replies`);
        
        if (response && response.data && response.data.replies) {
          // 返信データを設定
          setCommentReplies(prevReplies => ({
            ...prevReplies,
            [commentId]: response.data.replies
          }));
          
          // キャッシュに保存
          savePostStateToCache(id, { 
            commentReplies: {
              ...commentReplies,
              [commentId]: response.data.replies
            },
            showReplies: {
              ...showReplies,
              [commentId]: true
            }
          });
        }
      } catch (error) {
        
        // エラー時は表示状態を元に戻す
        setShowReplies(prevState => ({
          ...prevState,
          [commentId]: false
        }));
        toast.error(`返信の取得に失敗しました: ${error.response?.data?.error || error.message || 'APIエラー'}`);
      }
    }
  };

      // 投稿コンポーネントのuseEffectを最適化
  useEffect(() => {
    // 削除された投稿の場合はuseEffectをスキップ
    if (isDeleted) {
      
      
      // 削除状態をキャッシュからクリアする（ID競合防止）
      clearDeletedPostCache(id);
      return;
    }
    
    // マウント時に投稿の状態を初期化
    const initializePostState = async () => {
      
      
      // 削除された投稿の場合は処理をスキップし、キャッシュをクリア
      const cachedState = getPostStateFromCache(id);
      if (cachedState && cachedState.isDeleted) {
        
        clearDeletedPostCache(id);
      }
      
      try {
        // シフト情報があれば保存
        if (shifts && Array.isArray(shifts) && shifts.length > 0) {
          
          savePostStateToCache(id, { shifts: shifts });
        }
        // リロード時にシフト情報を取得
        else if (id && !isNaN(parseInt(id)) && !isDeleted) {
          try {
            
                      const token = localStorage.getItem('token');
          const response = await axios.get(`${config.API_BASE_URL}/api/posts/${id}?_t=${Date.now()}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
            
            if (response && response.data && response.data.shifts) {
              
              // シフト情報をキャッシュに保存し、状態も更新
              savePostStateToCache(id, { shifts: response.data.shifts });
              // ここでsetShiftsのようなステートがあれば直接更新
            }
          } catch (shiftError) {
            
            // 404エラーの場合は投稿が削除されたと見なす
            if (shiftError.response && shiftError.response.status === 404) {
              
              setIsDeleted(true);
              // キャッシュに保存しない（ID競合防止）
              clearDeletedPostCache(id);
            }
          }
        }
        
        // 投稿のステータス（いいね、ブックマーク、リツイート）を取得
        // Home.jsでユーザーアクションが既に読み込まれている場合はスキップ
        if (id && !isNaN(parseInt(id)) && !isDeleted && isLoggedIn && !userActionsLoaded) {
          try {
            const statusData = await getBatchedPostStatus(id);
            if (statusData) {
              // 状態を更新（ログ出力を簡素化）
              const isLiked = statusData.liked || statusData.is_liked || false;
              const isBookmarked = statusData.bookmarked || statusData.is_bookmarked || false;
              const isRetweeted = statusData.retweeted || statusData.is_retweeted || false;
              const likesCount = statusData.like_count || 0;
              const retweetsCount = statusData.retweet_count || 0;
              
              // 状態更新
              setLiked(isLiked);
              setBookmarked(isBookmarked);
              setRetweeted(isRetweeted);
              setLikeCount(likesCount);
              setRetweetCount(retweetsCount);
              
              // キャッシュに保存
              savePostStateToCache(id, {
                liked: isLiked,
                bookmarked: isBookmarked,
                retweeted: isRetweeted,
                likeCount: likesCount,
                retweetCount: retweetsCount,
                statusLoaded: true
              });
              
              console.log(`投稿${id}初期化完了:`, {
                liked: isLiked,
                bookmarked: isBookmarked,
                retweeted: isRetweeted,
                likes: likesCount,
                retweets: retweetsCount
              });
            }
          } catch (statusError) {
            console.error('ステータス取得エラー:', statusError);
          }
        } else if (!isLoggedIn) {
          console.log('未ログインのためステータス取得をスキップ');
        } else if (userActionsLoaded && userActions && userActions.loaded === true) {
          console.log(`投稿${id}: Home.jsで既にユーザーアクション読み込み済み - 重複取得をスキップ`);
        } else if (userActionsLoaded && (!userActions || userActions.loaded !== true)) {
          console.log(`投稿${id}: Home.jsのユーザーアクション未完了（loaded: ${userActions?.loaded}）- 個別取得実行`);
          // 個別のユーザーアクション取得を実行
          await getBatchedPostStatus(id);
        }
        
        // リツイート情報の初期設定
        if (isRetweet === true) {
          setRetweetInfo({
            isRetweet: true,
            retweeter: retweeter || null
          });
        }
        
        // 動画URLの正規化と検証
        if (video) {
          
          
          // videoRefの初期化を確認
          
        }
        
        // 投稿者情報の取得・更新処理
        if (authorId) {
          try {
            const token = localStorage.getItem('authToken');
            const userResponse = await axios.get(`${config.API_BASE_URL}/api/users/profile/${authorId}`, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            
            if (userResponse && userResponse.data) {
              const userData = userResponse.data;
              
              // 投稿ユーザーの場合、表示名を「源氏名＠店舗名」の形式に設定
              if (userData.role && (userData.role.name === 'poster' || userData.role === 'poster') && userData.poster_profile) {
                const posterProfile = userData.poster_profile;
                let formattedDisplayName = userData.display_name || userData.username;
                
                // 源氏名と店舗名が存在する場合は「源氏名＠店舗名」の形式で表示
                if (posterProfile.stage_name && posterProfile.shop_name) {
                  formattedDisplayName = `${posterProfile.stage_name}＠${posterProfile.shop_name}`;
                } 
                // 源氏名だけがある場合は「源氏名」のみ表示
                else if (posterProfile.stage_name) {
                  formattedDisplayName = posterProfile.stage_name;
                }
                // 店舗名だけがある場合は「ユーザー名＠店舗名」の形式で表示
                else if (posterProfile.shop_name) {
                  formattedDisplayName = `${userData.username}＠${posterProfile.shop_name}`;
                }
                
                // 投稿者情報を更新
                setPostAuthor(prevState => ({
                  ...prevState,
                  displayName: formattedDisplayName,
                  username: userData.username,
                  avatar: userData.avatar,
                  verified: userData.verified
                }));
                
                // キャッシュも更新
                savePostStateToCache(id, {
                  postAuthor: {
                    displayName: formattedDisplayName,
                    username: userData.username,
                    avatar: userData.avatar,
                    verified: userData.verified
                  }
                });
              }
            }
          } catch (error) {
            
          }
        }
        
        // コメント返信データのキャッシュがあれば初期化
        if (cachedState && cachedState.commentReplies) {
          setCommentReplies(cachedState.commentReplies);
        }
        
        // 返信表示状態のキャッシュがあれば初期化
        if (cachedState && cachedState.showReplies) {
          setShowReplies(cachedState.showReplies);
        }
        
        // コメントのいいね状態のキャッシュがあれば初期化
        if (cachedState && cachedState.commentLikes) {
          setCommentLikes(prevLikes => ({
            ...prevLikes,
            ...cachedState.commentLikes
          }));
        }
        
        // セッションストレージから非表示コメントを取得
        let hiddenCommentsIds = [];
        try {
          hiddenCommentsIds = JSON.parse(sessionStorage.getItem(`hiddenComments_${id}`) || '[]');
        } catch (storageError) {
          
        }
        
        // キャッシュからコメントリストを取得して、非表示コメントを除外
        if (cachedState && cachedState.commentsList && hiddenCommentsIds.length > 0) {
          const filteredComments = cachedState.commentsList.filter(
            comment => !hiddenCommentsIds.includes(comment.id)
          );
          
          // 調整されたコメント数
          const adjustedCount = Math.max(0, 
            (cachedState.commentsCount || cachedState.commentsList.length) - 
            (cachedState.commentsList.length - filteredComments.length)
          );
          
          // フィルタリングしたコメントリストで状態を更新
          setCommentsList(filteredComments);
          setCommentsCount(adjustedCount);
          
          // キャッシュにも保存
          savePostStateToCache(id, {
            commentsList: filteredComments,
            commentsCount: adjustedCount
          });
        }
        
        // 投票データの初期化 - 段階的にログを出力して初期化プロセスを確認
        
        
        
        
        // キャッシュされた投票データをまず確認
        if (cachedState && cachedState.pollData) {
          
          setPollData(cachedState.pollData);
          setHasVoted(cachedState.hasVoted || false);
        }
        
        // propsの投票データを優先的に使用
        if (poll) {
          
          setPollData(poll);
          setHasVoted(has_voted || false);
          
          // 投票データがある投稿として記録
          markPostWithPoll(id);
        } 
        // リロード時の投票データ取得 - has_pollフラグが立っていなくても、APIからのデータ取得を試行
        else if (!isNaN(parseInt(id))) {
          const numericPostId = parseInt(id);
          
          try {
            // 認証トークンを取得
            const authToken = localStorage.getItem('authToken');
            
            // API取得を試みる前に、すでに無効としてマークされているかをチェック
            if (window.INVALID_POST_IDS && window.INVALID_POST_IDS.has(numericPostId)) {
              
              return; // 早期リターンで以降の処理をスキップ
            }
            
            // APIエンドポイントテンプレートを取得（グローバル変数またはデフォルト）
            const endpointTemplates = window.POLL_API_ENDPOINTS || [
              { url: '/api/posts/:id/poll', method: 'GET' }
            ];
            
            // IDを置換して実際のエンドポイントを生成
            const apiEndpoints = endpointTemplates.map(endpoint => {
              const cleanUrl = endpoint.url.replace(':id', numericPostId);
              const fullUrl = `${config.API_BASE_URL}${cleanUrl.startsWith('/') ? cleanUrl : '/' + cleanUrl}`;
              return {
                url: fullUrl,
                method: endpoint.method || 'GET'
              };
            });
            
            
            
            // 各エンドポイントを順番に試行
            let successResponse = null;
            let lastError = null;
            
            // 少なくとも1つのエンドポイントで試行
            for (const endpoint of apiEndpoints) {
              try {
                              
              
              let response;
              if (endpoint.method === 'GET') {
                
                response = await axios.get(endpoint.url, {
                  headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {},
                  params: { _t: Date.now() } // キャッシュ回避
                });
              }
                
                if (response && response.data) {
                  // APIレスポンスがオブジェクトで、optionsプロパティを持つか確認
                  let validPollData = response.data;
                  
                  // レスポンスがデータラッパーの場合、中身を取り出す
                  if (response.data.data && typeof response.data.data === 'object') {
                    validPollData = response.data.data;
                  }
                  
                  // optionsが存在するか、parsableな文字列かを確認
                  if (validPollData.options) {
                    let parsedOptions = validPollData.options;
                    
                    // 文字列の場合はJSONとして解析
                    if (typeof validPollData.options === 'string') {
                      try {
                        parsedOptions = JSON.parse(validPollData.options);
                        validPollData.options = parsedOptions;
                      } catch (e) {
                        
                      }
                    }
                    
                    
                    setPollData(validPollData);
                    setHasVoted(validPollData.user_vote !== null);
                    savePostStateToCache(id, { 
                      pollData: validPollData,
                      hasVoted: validPollData.user_vote !== null
                    });
                    // 投票データがある投稿としてマーク
                    markPostWithPoll(numericPostId);
                    foundValidEndpoint = true;
                    break;
                  } else {
                    
                  }
                }
              } catch (err) {
                // 404エラー (Not Found)や405エラー (Method Not Allowed)の場合は正常とみなす
                if (err.response && (err.response.status === 404 || err.response.status === 405)) {
                  // 投票データが存在しない投稿（正常な状態）
                  continue; // 次のエンドポイントを試す
                }
                // その他のエラーは記録
                lastError = err;
              }
            }
            
            // いずれかのAPIが成功した場合
            if (successResponse) {
              // 投票データを取得
              const pollData = successResponse.data;
              
              // 標準形式の検証 - 最低限options配列があることを確認
              if (!pollData.options || !Array.isArray(pollData.options)) {
                
                return; // 早期リターン
              }
              
              // 投票総数が未定義の場合、個別の投票数を合計する
              if (pollData.total_votes === undefined && Array.isArray(pollData.options)) {
                pollData.total_votes = pollData.options.reduce((sum, option) => 
                  sum + (option.votes || 0), 0);
                
              }
              
              // 投票データを設定
              setPollData(pollData);
              setHasVoted(pollData.user_vote !== null);
              
              // 投票データをキャッシュに保存
              savePostStateToCache(id, {
                pollData: pollData,
                hasVoted: pollData.user_vote !== null
              });
              
              // 投票データを持つ投稿としてマーク
              markPostWithPoll(numericPostId);
            } else if (lastError) {
              
              
            }
          } catch (error) {
            
            
          }
        }
      } catch (error) {
        
      }
    };

    // 投稿の状態を初期化
    initializePostState();
    
    // リツイート状態の属性を更新
    updateRetweetedAttribute(id, retweeted);
    
    // 非同期処理のためのクリーンアップ関数
    return () => {
      // コンポーネントがアンマウントされた時の処理
      
    };
  }, [id, isDeleted]); // id, isDeletedを依存配列に追加して、IDが変わるか投稿が削除されたときに再初期化する

  // ステートを更新したときにキャッシュも更新
  useEffect(() => {
    // 現在の状態をキャッシュに保存
    const currentState = {
      liked,
      likeCount,
      retweeted,
      retweetCount,
      bookmarked,
      commentsList,
      commentsCount,
      isDeleted,
      isFollowing,
      postAuthor,
      retweetInfo,
      isPinnedPost: isPinned || isPinnedPost, // props経由のisPinnedを優先
      pollData, // 投票データをキャッシュに保存
      hasVoted,  // 投票状態をキャッシュに保存
      commentReplies, // 返信データもキャッシュに保存
      showReplies, // 返信表示状態もキャッシュに保存
      shifts: shifts // シフト情報も保存
    };
    
    savePostStateToCache(id, currentState);
  }, [
    id, liked, likeCount, retweeted, retweetCount, bookmarked, 
    commentsList, commentsCount, isDeleted, isFollowing, postAuthor, retweetInfo, isPinnedPost, isPinned,
    pollData, hasVoted, commentReplies, showReplies, shifts // シフト情報も監視
  ]);

  // メニューに関するuseEffect
  useEffect(() => {
    // メニューが閉じたときにbodyに残っているスタイルを解除する
    if (!menuOpen && document.body.style.overflow === 'hidden') {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  }, [menuOpen]);

  // propsが変更された場合に状態を同期
  useEffect(() => {
    console.log('props監視:', { 
      initialRetweeted, 
      likeCount: likes,
      liked: initialLiked,
      bookmarked: initialBookmarked
    });
    
    // リツイート状態の同期
    if (initialRetweeted !== undefined && initialRetweeted !== retweeted) {
      console.log('propsからリツイート状態同期:', initialRetweeted);
      setRetweeted(initialRetweeted);
      savePostStateToCache(id, { retweeted: initialRetweeted });
    }
    
    // いいね状態の同期
    if (initialLiked !== undefined && initialLiked !== liked) {
      console.log('propsからいいね状態同期:', initialLiked);
      setLiked(initialLiked);
      savePostStateToCache(id, { liked: initialLiked });
    }
    
    // ブックマーク状態の同期
    if (initialBookmarked !== undefined && initialBookmarked !== bookmarked) {
      console.log('propsからブックマーク状態同期:', initialBookmarked);
      setBookmarked(initialBookmarked);
      savePostStateToCache(id, { bookmarked: initialBookmarked });
    }
    
    // いいね数の同期
    if (likes !== undefined && likes !== likeCount) {
      console.log('propsからいいね数同期:', likes);
      setLikeCount(likes);
      savePostStateToCache(id, { likeCount: likes });
    }
    
    // リツイート数の同期
    if (retweets !== undefined && retweets !== retweetCount) {
      console.log('propsからリツイート数同期:', retweets);
      setRetweetCount(retweets);
      savePostStateToCache(id, { retweetCount: retweets });
    }
  }, [initialRetweeted, initialLiked, initialBookmarked, likes, retweets, id, retweeted, liked, bookmarked, likeCount, retweetCount]);

  // 全てのフックを定義してから、レンダリングの条件分岐を行う
  const handleLike = async () => {
    // ゲストユーザーの場合はポップアップ表示
    if (isGuest) {
      showGuestWarning();
      return;
    }
    
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    // いいね権限がなければ権限エラーページにリダイレクト
    if (!canLike) {
      navigate('/unauthorized');
      return;
    }
    
    // 既にローディング中の場合は処理しない
    if (likeLoading) {
      return;
    }
    
    // スコープの問題を解決するため、try-catchブロックの外で変数を定義
    const wasLiked = liked;
    const prevLikeCount = likeCount;
    
    try {
      setLikeLoading(true);
      
      if (wasLiked) {
        // いいね解除の処理
        console.log(`🔄 [UNLIKE] Starting unlike process for post: ${id}`);
        
        // 先にUIを更新（即時フィードバックのため）
        setLiked(false);
        setLikeCount(Math.max(0, prevLikeCount - 1));
        
        // APIリクエスト
        const response = await apiService.delete(`/api/posts/${id}/like`);
        
        console.log('✅ [UNLIKE] Unlike completed successfully:', response);
        
        // APIレスポンスから正確な値を取得
        if (response && response.data) {
          const actualLiked = false;
          const actualLikeCount = typeof response.data.likes_count === 'number' 
            ? response.data.likes_count 
            : Math.max(0, prevLikeCount - 1);
          
          // 楽観的更新と異なる値の場合のみ状態を更新（描画の点滅を防ぐ）
          if (actualLiked !== false) {
            setLiked(actualLiked);
          }
          if (actualLikeCount !== Math.max(0, prevLikeCount - 1)) {
            setLikeCount(actualLikeCount);
          }
          
          // 成功したら状態をキャッシュに保存
          savePostStateToCache(id, {
            liked: actualLiked,
            likeCount: actualLikeCount
          });
          
          // ユーザーアクションキャッシュを更新
          updateUserActionCache(id, 'liked', actualLiked);
        }
        
      } else {
        // いいね追加の処理
        console.log(`🔄 [LIKE] Starting like process for post: ${id}`);
        
        // 1. 楽観的UI更新（見た目は即座に更新）
        setLiked(true);
        setLikeCount(prevLikeCount + 1);
        
        // 2. APIリクエストでいいねを保存
        const response = await apiService.post(`/api/posts/${id}/like`);
        
        console.log('✅ [LIKE] Like saved to database successfully:', response);
        console.log('🔍 [DEBUG] Response data structure:', response.data);
        
        // 3. APIレスポンスから正確な値を取得
        if (response && response.data) {
          const actualLiked = response.data.liked || response.data.is_liked || true;
          const actualLikeCount = typeof response.data.likes_count === 'number' 
            ? response.data.likes_count 
            : prevLikeCount + 1;
          
          // 楽観的更新と異なる値の場合のみ状態を更新（描画の点滅を防ぐ）
          if (actualLiked !== true) {
            setLiked(actualLiked);
          }
          if (actualLikeCount !== (prevLikeCount + 1)) {
            setLikeCount(actualLikeCount);
          }
          
          // 4. プッシュ通知が必要かバックエンドからの情報で判断
          if (response.data.notification_needed && response.data.receiver_id) {
            console.log(`🔔 [LIKE] Sending push notification for post: ${id}, receiver: ${response.data.receiver_id}`);
            try {
              const pushResponse = await apiService.post('/api/notifications/sync-push/like', {
                post_id: id,
                receiver_id: response.data.receiver_id
              }, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
              });
              console.log('✅ [LIKE] Push notification sent successfully:', pushResponse);
            } catch (pushError) {
              console.warn('⚠️ [LIKE] Push notification failed (ignored):', pushError);
            }
          } else {
            console.log('ℹ️ [LIKE] No push notification needed (notification_needed=false or own post)');
          }
          
          // 成功したら状態をキャッシュに保存（実際の値で）
          savePostStateToCache(id, {
            liked: actualLiked,
            likeCount: actualLikeCount
          });
          
          // ユーザーアクションキャッシュを更新
          updateUserActionCache(id, 'liked', actualLiked);
        }
      }
      
      // キャッシュを強制的にクリア
      clearPostCache(id);
      
      // 親コンポーネント（Home.js）のonLike関数も呼び出し、userActionsを同期更新
      if (onLike && typeof onLike === 'function') {
        onLike(id);
      }
      
      console.log(`Post.js: いいね処理完了 - 投稿${id}, liked: ${liked}, count: ${likeCount}`);
      
    } catch (error) {
      console.error('=== Post.js いいね処理エラー詳細 ===');
      console.error('エラーオブジェクト全体:', error);
      console.error('エラーメッセージ:', error.message);
      console.error('エラータイプ:', error.constructor.name);
      console.error('エラーレスポンス:', error.response);
      console.error('エラーデータ:', error.response?.data);
      console.error('エラーステータス:', error.response?.status);
      console.error('エラーヘッダー:', error.response?.headers);
      console.error('リクエスト設定:', error.config);
      console.error('投稿ID:', id);
      console.error('前の状態:', { wasLiked, prevLikeCount });
      console.error('===========================');
      
      // エラーが発生した場合は状態を元に戻す
      setLiked(wasLiked);
      setLikeCount(prevLikeCount);
      
      // 認証エラーの場合はログイン画面へ
      if (error.response && error.response.status === 401) {
        navigate('/login');
      }
    } finally {
      setLikeLoading(false);
    }
  };
  
  const handleRetweet = async () => {
    // ゲストユーザーの場合はポップアップ表示
    if (isGuest) {
      showGuestWarning();
      return;
    }
    
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    // リツイート権限がなければ権限エラーページにリダイレクト
    if (!canRetweet) {
      navigate('/unauthorized');
      return;
    }
    
    // 既にローディング中の場合は処理しない
    if (retweetLoading) {
      return;
    }
      
          try {
      setRetweetLoading(true);
      
      // リツイート状態を更新する前の値を保存
      const wasRetweeted = retweeted;
      const prevRetweetCount = retweetCount;
      
      // 先に状態を更新（UI即時反映のため）
      setRetweeted(!wasRetweeted);
      setRetweetCount(wasRetweeted ? Math.max(0, prevRetweetCount - 1) : prevRetweetCount + 1);
      
      // 特定の投稿のリツイートボタンのdata-retweeted属性を更新
      updateRetweetedAttribute(id, !wasRetweeted);
      
      // APIリクエスト
      let response;
      if (wasRetweeted) {
        // リツイート解除の処理
        response = await apiService.delete(`/api/posts/${id}/retweet`);
        
        // リツイート解除の場合、親コンポーネントに通知
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          const event = new CustomEvent('retweet_update', { 
            detail: { 
              postId: id, 
              retweeted: false,
              author: {
                username: postAuthor.username,
                displayName: postAuthor.displayName,
                avatar: postAuthor.avatar,
                verified: postAuthor.verified
              }
            } 
          });
          window.dispatchEvent(event);
        }
      } else {
        // リツイートの処理
        response = await apiService.post(`/api/posts/${id}/retweet`);
        
        // リツイート成功の場合、親コンポーネントに通知
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          const event = new CustomEvent('retweet_update', { 
            detail: { 
              postId: id, 
              retweeted: true,
              author: {
                username: postAuthor.username,
                displayName: postAuthor.displayName,
                avatar: postAuthor.avatar,
                verified: postAuthor.verified
              }
            } 
          });
          window.dispatchEvent(event);
        }
      }
      
      // APIレスポンスから正確な状態を取得
      if (response && response.data) {
        // retweetedとis_retweetedの両方をチェック
        const actualRetweeted = response.data.retweeted === true || 
                              response.data.is_retweeted === true || 
                              !wasRetweeted;
        
        // retweets_countとretweet_countの両方をチェック
        const actualCount = typeof response.data.retweets_count === 'number' 
          ? response.data.retweets_count 
          : typeof response.data.retweet_count === 'number'
          ? response.data.retweet_count
          : (wasRetweeted ? Math.max(0, prevRetweetCount - 1) : prevRetweetCount + 1);
        
        // 正確な値で状態を更新
        setRetweeted(actualRetweeted);
        setRetweetCount(actualCount);
        
        // 成功したら状態をキャッシュに保存
        savePostStateToCache(id, {
          retweeted: actualRetweeted,
          retweetCount: actualCount
        });
        
        // ユーザーアクションキャッシュを更新
        updateUserActionCache(id, 'retweeted', actualRetweeted);
        
        // リツイート状態の属性を更新
        updateRetweetedAttribute(id, actualRetweeted);
        
        // 親コンポーネント（Home.js）のonRetweet関数も呼び出し、userActionsを同期更新
        if (onRetweet && typeof onRetweet === 'function') {
          onRetweet(id);
        }
      } else {
        // APIレスポンスに具体的な情報がない場合は、UIで設定した値をキャッシュに保存
        const newRetweetCount = wasRetweeted ? Math.max(0, prevRetweetCount - 1) : prevRetweetCount + 1;
        savePostStateToCache(id, {
          retweeted: !wasRetweeted,
          retweetCount: newRetweetCount
        });
        
        // ユーザーアクションキャッシュを更新
        updateUserActionCache(id, 'retweeted', !wasRetweeted);
        
        // 親コンポーネント（Home.js）のonRetweet関数も呼び出し、userActionsを同期更新
        if (onRetweet && typeof onRetweet === 'function') {
          onRetweet(id);
        }
      }
    } catch (error) {
      
      
      // エラーが発生した場合は状態を元に戻す
      setRetweeted(retweeted);
      setRetweetCount(retweetCount);
      
      // 認証エラーの場合はログインページへ
      if (error.response && error.response.status === 401) {
        navigate('/login');
      }
    } finally {
      setRetweetLoading(false);
    }
  };

  // メニューを開く処理
  const handleMenuOpen = (event) => {
    // stopPropagationのみ残す（preventDefaultを削除）
    event.stopPropagation();
    
    // メニュー状態を更新
    setMenuAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  // メニューを閉じる処理
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuOpen(false);
  };

  // 投稿削除の処理
  const handleDeletePost = async () => {
    if (!canDeletePost) return;
    
    try {
      setIsDeleting(true);
      handleMenuClose();
      
      
      
      // 投稿IDを削除済みリストに追加
      saveDeletedPostId(id);
      
      // 楽観的UI更新 - 先に削除済みフラグを設定（キャッシュには保存しない）
      setIsDeleted(true);
      // キャッシュから削除フラグをクリア（ID競合防止）
      clearDeletedPostCache(id);
      
      try {
        // safeApiCall.deleteを使用
        const response = await safeApiCall.delete(`/api/posts/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });
        
        
        
        // 削除成功イベントを発火
        if (onDelete) onDelete(id);
        
        // 成功メッセージを表示
        toast.success('投稿が削除されました');
      } catch (error) {
        
        
        // エラーの種類に応じた処理
        if (error.response) {
          // サーバーからのレスポンスがある場合
          if (error.response.status === 404) {
            // 404エラー - 投稿が既に削除されている
            
            
            // 削除成功イベントを発火（既に削除されている場合も成功とみなす）
            if (onDelete) onDelete(id);
            
            // 情報メッセージを表示
            toast.info('投稿はすでに削除されています');
          } else {
            // その他のHTTPエラー
            
            
            // 削除成功イベントは発火する（UIからは削除）
            if (onDelete) onDelete(id);
            
            // 警告メッセージを表示
            toast.warn('サーバーからエラーレスポンスを受信しましたが、投稿は削除されました');
          }
        } else if (error.request) {
          // リクエストは送信されたがレスポンスがない場合（ネットワークエラーなど）
          
          
          // 削除成功イベントは発火する（UIからは削除）
          if (onDelete) onDelete(id);
          
          // 警告メッセージを表示
          toast.warn('ネットワークエラーが発生しましたが、投稿は削除されました');
        } else {
          // リクエスト設定中のエラー
          
          
          // 削除成功イベントは発火する（UIからは削除）
          if (onDelete) onDelete(id);
          
          // 警告メッセージを表示
          toast.warn('エラーが発生しましたが、投稿は削除されました');
        }
      } finally {
        setIsDeleting(false);
      }
    } catch (error) {
      
      setIsDeleting(false);
      toast.error(`投稿の削除中にエラーが発生しました: ${error.message}`);
    }
  };

  // ブックマーク/保存処理
  const handleBookmark = async () => {
    // ゲストユーザーの場合はポップアップ表示
    if (isGuest) {
      showGuestWarning();
      return;
    }
    
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    // 既にローディング中の場合は処理しない
    if (bookmarkLoading) {
      return;
    }
    
    try {
      setBookmarkLoading(true);
      const wasBookmarked = bookmarked;
      
      // 先にUIを更新
      setBookmarked(!wasBookmarked);
      
      if (wasBookmarked) {
        await apiService.delete(`/api/posts/${id}/bookmark`);
        if (onRemoveSave) onRemoveSave(id);
      } else {
        await apiService.post(`/api/posts/${id}/bookmark`);
        if (onSave) onSave(id);
      }
      
      // キャッシュに保存
      savePostStateToCache(id, { bookmarked: !wasBookmarked });
      
      // ユーザーアクションキャッシュを更新
      updateUserActionCache(id, 'bookmarked', !wasBookmarked);
      
      // 親コンポーネント（Home.js）のonSave/onRemoveSave関数も呼び出し、userActionsを同期更新
      // Note: onSave/onRemoveSaveは既に上で呼ばれているが、userActions同期のため追加で呼び出し
    } catch (error) {
      
      setBookmarked(bookmarked); // エラーの場合は元に戻す
      
      if (error.response && error.response.status === 401) {
        navigate('/login');
      }
    } finally {
      setBookmarkLoading(false);
    }
  };

  // コメントフォームの表示/非表示を切り替え
  const toggleCommentForm = () => {
    // ゲストユーザーの場合はポップアップ表示
    if (isGuest) {
      showGuestWarning();
      return;
    }
    
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    setShowCommentForm(!showCommentForm);
    setCommentError('');
  };

  // コメント投稿処理
  const handlePostComment = async () => {
    if (!commentText.trim()) {
      setCommentError('コメントを入力してください');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // 1. 楽観的UI更新（先にコメントを表示）
      const tempComment = {
        id: `temp_${Date.now()}`,
        text: commentText,
        user: {
          id: currentUser?.id,
          username: currentUser?.username,
          display_name: currentUser?.display_name || currentUser?.username,
          avatar: currentUser?.avatar
        },
        created_at: new Date().toISOString(),
        likes_count: 0,
        replies_count: 0
      };
      
      setCommentsList([tempComment, ...commentsList]);
      setCommentsCount(prevCount => prevCount + 1);
      setShowComments(true);
      
      // 2. APIリクエストでコメントを保存
      const response = await apiService.post(`/api/posts/${id}/comments`, {
        text: commentText
      });
      
      console.log('✅ [COMMENT] Comment saved to database successfully:', response);
      
      const newComment = response.data.comment || response.data;
      
      // 3. 一時的なコメントを実際のコメントに置き換え（関数形式で最新stateを参照）
      setCommentsList(prevComments => 
        prevComments.map(comment => 
          comment.id === tempComment.id ? { ...newComment, user: newComment.user || tempComment.user } : comment
        )
      );
      
      // 4. プッシュ通知が必要かバックエンドからの情報で判断
      if (response.data.notification_needed && response.data.receiver_id) {
        console.log(`🔔 [COMMENT] Sending push notification for post: ${id}, receiver: ${response.data.receiver_id}`);
        try {
          const pushResponse = await apiService.post('/api/notifications/sync-push/comment', {
            post_id: id,
            comment_id: newComment.id,
            receiver_id: response.data.receiver_id
          }, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });
          console.log('✅ [COMMENT] Push notification sent successfully:', pushResponse);
        } catch (pushError) {
          console.warn('⚠️ [COMMENT] Push notification failed (ignored):', pushError);
        }
      } else {
        console.log('ℹ️ [COMMENT] No push notification needed (notification_needed=false or own post)');
      }
      
      // フォームをリセット
      setCommentText('');
      setShowCommentForm(false);
      
      // キャッシュを更新
      savePostStateToCache(id, { 
        commentsList: updatedComments,
        commentsCount: commentsCount + 1,
        showComments: true
      });
      
      // 最新のコメントリストを取得（必要に応じて）
      // fetchComments();
      
      // コールバックを呼び出し
      if (onComment) onComment(id, newComment);
      
    } catch (error) {
      console.warn('⚠️ [COMMENT] API error occurred (but comment might be saved):', error);
      
      // 楽観的描画のため、500エラーでもUIはそのまま維持
      // （実際にコメントが保存されている可能性が高いため）
      
      // 認証エラーの場合のみロールバックとリダイレクト
      if (error.response && error.response.status === 401) {
        // 楽観的更新を元に戻す
        setCommentsList(commentsList);
        setCommentsCount(prevCount => Math.max(0, prevCount - 1));
        setCommentError('認証エラーが発生しました。再ログインしてください。');
        navigate('/login');
      } else {
        // その他のエラーは警告メッセージのみ表示（UIは維持）
        console.log('✅ [COMMENT] Optimistic UI maintained despite API error');
        // フォームはリセットしてユーザーの混乱を防ぐ
        setCommentText('');
        setShowCommentForm(false);
        
        // キャッシュを更新（楽観的更新されたデータを保持）
        savePostStateToCache(id, { 
          commentsList: [tempComment, ...commentsList],
          commentsCount: commentsCount + 1,
          showComments: true
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // コメント削除関数
  const handleDeleteComment = async (commentId) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    // 削除確認
    if (!window.confirm('このコメントを削除しますか？')) {
      return;
    }
    
    try {
      // APIを使用してコメントを削除
      await apiService.delete(`/api/comments/${commentId}`);
      
      // コメントリストから削除
      const updatedComments = commentsList.filter(comment => comment.id !== commentId);
      setCommentsList(updatedComments);
      setCommentsCount(prevCount => Math.max(0, prevCount - 1));
      
      // 返信データも更新
      if (commentReplies[commentId]) {
        const newCommentReplies = {...commentReplies};
        delete newCommentReplies[commentId];
        setCommentReplies(newCommentReplies);
      }
      
      // キャッシュを更新
      savePostStateToCache(id, { 
        commentsList: updatedComments,
        commentsCount: Math.max(0, commentsCount - 1),
        commentReplies: {...commentReplies, [commentId]: undefined}
      });
      
      // 成功メッセージ
      toast.success('コメントを削除しました');
    } catch (error) {
      
      
      // エラーレスポンスの詳細をログに出力
      if (error.response) {
        
        
      }
      
      // ユーザーにエラーメッセージを表示
      toast.error(`コメントの削除に失敗しました: ${error.response?.data?.error || error.message || 'APIエラー'}`);
      
      // 認証エラーの場合はログイン画面へ
      if (error.response && error.response.status === 401) {
        navigate('/login');
      }
    }
  };
  
  // コメントいいね関数
  const handleLikeComment = async (commentId) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    try {
      const isLiked = commentLikes[commentId];
      
      // UIを先に更新（楽観的更新）
      setCommentLikes(prev => ({
        ...prev,
        [commentId]: !isLiked
      }));
      
      // APIリクエスト
      let response;
      if (isLiked) {
        response = await apiService.delete(`/api/comments/${commentId}/like`);
      } else {
        response = await apiService.post(`/api/comments/${commentId}/like`);
      }
      
      // バックエンドから返される正確な状態を反映
      if (response && response.data) {
        const { liked, likes_count } = response.data;
        
        // 正確な状態で更新
        setCommentLikes(prev => ({
          ...prev,
          [commentId]: liked
        }));
        
        // コメントリストの該当コメントのいいね数も更新
        setCommentsList(prevComments => 
          prevComments.map(comment => 
            comment.id === commentId 
              ? { ...comment, liked: liked, likes_count: likes_count }
              : comment
          )
        );
        
        // キャッシュにも保存
        const updatedLikes = { ...commentLikes, [commentId]: liked };
        savePostStateToCache(id, { commentLikes: updatedLikes });
      }
      
    } catch (error) {
      console.error('コメントいいねエラー:', error);
      
      // 500エラーやネットワークエラーの場合は楽観的UIを維持
      if (!error.response || error.response.status >= 500) {
        // 500エラーの場合は実際には成功している可能性が高いため、UIを元に戻さない
        console.log('500エラーまたはネットワークエラー: 楽観的UIを維持');
        return;
      }
      
      // 認証エラー（401/403）や400番台エラーの場合は元に戻す
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        setCommentLikes(prev => ({
          ...prev,
          [commentId]: !prev[commentId]
        }));
        navigate('/login');
      } else if (error.response && error.response.status >= 400 && error.response.status < 500) {
        // その他の400番台エラーの場合は元に戻す
        setCommentLikes(prev => ({
          ...prev,
          [commentId]: !prev[commentId]
        }));
      }
    }
  };

  // 投稿クリック時の処理
  const handlePostClick = (e) => {
    // プロファイルカードが表示されている場合は何もしない
    if (showProfileCard) return;
    
    // 明示的にnavigate関数にリンク先を渡す
    if (!viewOnly) {
      navigate(`/post/${id}`);
    }
  };

  // アバタークリックの処理を追加
  const handleAvatarClick = (e) => {
    e.stopPropagation();
    
    // プロフィールページへナビゲート
    if (username) {
      checkTokenBeforeNavigate(`/profile/${username}`);
    }
  };

  // プロフィールカード表示処理
  const showProfileCardHandler = async (e) => {
    // イベントの伝播を停止
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (hideCardTimeoutId) {
      clearTimeout(hideCardTimeoutId);
      setHideCardTimeoutId(null);
    }

    // 現在ホバーされているアバター要素の位置を取得
    if (e && e.currentTarget) {
      const hoveredAvatar = e.currentTarget;
      const postElement = hoveredAvatar.closest('.post');
      
      if (hoveredAvatar && postElement) {
        // アバター要素とpost要素の位置情報を取得
        const avatarRect = hoveredAvatar.getBoundingClientRect();
        const postRect = postElement.getBoundingClientRect();
        
        // 実際に適用する位置を計算
        // アバターの上端からpost上端までの距離に合わせる
        const relativeTop = avatarRect.top - postRect.top;
        // アバターの右端からpost左端までの距離 + 追加のマージン
        const relativeLeft = avatarRect.right - postRect.left + 10;
        
        // プロフィールカードの位置を設定
        setProfileCardPosition({
          top: relativeTop,
          left: relativeLeft
        });
      }
    }
    
    // ユーザーのusernameが必要
    if (!username) {
      
      return;
    }
    
    const requestKey = `userProfileRequest_${username}`;
    
    // 重複リクエスト防止
    if (window[requestKey]) {
      return;
    }
    
    window[requestKey] = true;
    
    try {
      // プロフィールカードを表示するための最小限の準備
      setShowProfileCard(true);
      
      // トークンを取得 - 認証コンテキストから直接取得
      const authToken = token || localStorage.getItem('authToken');
      
      
      // キャッシュを一時的に無視して常に最新のフォロー状態を取得する
      let followingFromAPI = false;
      
      // リクエスト1: まず明示的にフォロー状態を確認するAPI（より信頼性が高い）
      if (isLoggedIn && currentUser && authToken) {
        // 本人のプロフィールカードを開いた場合はフォロー状態のチェックをスキップ
        if (currentUser.username === username) {
          
          setIsFollowing(false); // 自分自身はフォローできない
        } else {
          try {
            const followEndpoint = `api/social/following/${username}`;
            
            
            const followResponse = await axios.get(`${config.API_BASE_URL}/${followEndpoint}`, {
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              },
              params: {
                _t: Date.now() // キャッシュ回避
              }
            });
            
            if (followResponse && followResponse.data) {
              followingFromAPI = Boolean(followResponse.data.is_following);
              
              
              // フォロー状態が取得できたら即座に状態を更新
              setIsFollowing(followingFromAPI);
              
              // キャッシュも更新
              if (typeof window !== 'undefined' && window.followStateCache) {
                window.followStateCache.set(username, followingFromAPI);
              }
            }
          } catch (followCheckError) {
            
            
            // エラーコードが409の場合は「既にフォロー中」を意味する
            if (followCheckError.response && followCheckError.response.status === 409) {
              followingFromAPI = true;
              
              
              // フォロー状態を更新
              setIsFollowing(true);
              
              // キャッシュも更新
              if (typeof window !== 'undefined' && window.followStateCache) {
                window.followStateCache.set(username, true);
              }
            }
          }
          
          // リクエスト1.5: もしfollowingから判断できなかった場合、followersエンドポイントも確認
          if (!followingFromAPI) {
            try {
              const followersEndpoint = `api/social/followers/${username}`;
              
              
              const followersResponse = await axios.get(`${config.API_BASE_URL}/${followersEndpoint}`, {
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Accept': 'application/json',
                  'Content-Type': 'application/json'
                },
                params: {
                  _t: Date.now() // キャッシュ回避
                }
              });
              
              if (followersResponse && followersResponse.data) {
                // レスポンスの形式によって処理を分岐
                // パターン1: フォロワーリストが配列として直接返される場合
                if (Array.isArray(followersResponse.data)) {
                  const followers = followersResponse.data;
                  const isCurrentUserFollowing = followers.some(
                    follower => follower.username === currentUser.username || follower.id === currentUser.id
                  );
                  if (isCurrentUserFollowing) {
                    followingFromAPI = true;
                    
                    setIsFollowing(true);
                    
                    // キャッシュも更新
                    if (typeof window !== 'undefined' && window.followStateCache) {
                      window.followStateCache.set(username, true);
                    }
                  }
                }
                // パターン2: followers配列がオブジェクト内にネストされている場合
                else if (followersResponse.data.followers && Array.isArray(followersResponse.data.followers)) {
                  const followers = followersResponse.data.followers;
                  const isCurrentUserFollowing = followers.some(
                    follower => follower.username === currentUser.username || follower.id === currentUser.id
                  );
                  if (isCurrentUserFollowing) {
                    followingFromAPI = true;
                    
                    setIsFollowing(true);
                    
                    // キャッシュも更新
                    if (typeof window !== 'undefined' && window.followStateCache) {
                      window.followStateCache.set(username, true);
                    }
                  }
                }
                // パターン3: フォロワーリストを持つオブジェクトが返される場合
                else if (followersResponse.data.users && Array.isArray(followersResponse.data.users)) {
                  const followers = followersResponse.data.users;
                  const isCurrentUserFollowing = followers.some(
                    follower => follower.username === currentUser.username || follower.id === currentUser.id
                  );
                  if (isCurrentUserFollowing) {
                    followingFromAPI = true;
                    
                    setIsFollowing(true);
                    
                    // キャッシュも更新
                    if (typeof window !== 'undefined' && window.followStateCache) {
                      window.followStateCache.set(username, true);
                    }
                  }
                }
                // パターン4: is_following属性がレスポンスに含まれている場合
                else if (followersResponse.data.is_following !== undefined) {
                  followingFromAPI = Boolean(followersResponse.data.is_following);
                  
                  setIsFollowing(followingFromAPI);
                  
                  // キャッシュも更新
                  if (typeof window !== 'undefined' && window.followStateCache) {
                    window.followStateCache.set(username, followingFromAPI);
                  }
                }
              }
            } catch (followersCheckError) {
              
            }
          }
        }
      }
      
      // リクエスト2: ユーザープロフィール情報を取得（キャッシュ機能付き）
      try {
        const profileData = await getUserProfile(username);
        
        if (profileData) {
          // フォロー状態を確認（既に専用APIから確認済みの場合は上書きしない）
          let profileFollowingState = followingFromAPI;
          
          // プロフィールAPIからもフォロー状態を取得（専用APIからの結果がない場合のみ使用）
          if (!followingFromAPI && profileData.is_following !== undefined) {
            profileFollowingState = Boolean(profileData.is_following);
            
            setIsFollowing(profileFollowingState);
          }
          
          // プロフィールデータを設定
          const enrichedProfileData = {
            ...profileData,
            is_following: profileFollowingState // 専用APIまたはプロフィールAPIからのフォロー状態を設定
          };
          
          // プロフィールデータをセット
          setProfileData(enrichedProfileData);
          
          // グローバルキャッシュも更新
          if (typeof window !== 'undefined') {
            if (!window.profileDataCache) window.profileDataCache = {};
            window.profileDataCache[username] = enrichedProfileData;
          }
          
          // フォロー状態のキャッシュも更新（既に更新されている場合は不要）
          if (typeof window !== 'undefined' && window.followStateCache) {
            window.followStateCache.set(username, profileFollowingState);
          }
          
          
        }
      } catch (profileError) {
        
        
        // 最小限のプロフィール情報でも表示を続ける
        if (!profileData) {
          const minimalProfileData = {
            username: username,
            display_name: displayName || username,
            avatar: avatar || null,
            bio: "プロフィール情報を読み込めませんでした",
            is_following: followingFromAPI // 専用APIからの結果を反映
          };
          setProfileData(minimalProfileData);
        }
      }
    } catch (error) {
      
      
      // エラーの場合でも最小限の情報で表示
      if (!profileData) {
        const minimalProfileData = {
          username: username,
          display_name: displayName || username,
          avatar: avatar || null,
          bio: "読み込みエラー"
        };
        setProfileData(minimalProfileData);
        setIsFollowing(false);
      }
    } finally {
      // リクエスト中フラグを解除
      if (typeof window !== 'undefined') {
        window[requestKey] = false;
      }
    }
  };

  // プロフィールカードを隠す処理
  const hideProfileCard = () => {
    const timeoutId = setTimeout(() => {
      setShowProfileCard(false);
    }, 300);
    
    setHideCardTimeoutId(timeoutId);
  };

  // フォロー状態を更新する処理
  const toggleFollow = async (e) => {
    e.stopPropagation();
    
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    // 自分自身をフォローしようとしている場合は何もしない
    if (currentUser.username === username) {
      
      return;
    }
    
    setIsFollowLoading(true);
    
    try {
      // 現在のフォロー状態を保存
      const currentFollowState = isFollowing;
      
      
      // 即時UI更新のため先に状態を変更
      setIsFollowing(!currentFollowState);
      
      // トークンを取得 - 認証コンテキストから直接取得
      const authToken = token || localStorage.getItem('token');
      
      // フォロー/アンフォローのエンドポイントを決定
      let endpoint;
      if (currentFollowState) {
        // フォロー中ならアンフォローエンドポイント
        endpoint = `/api/social/unfollow/${username}`;
        
      } else {
        // フォローしていなければフォローエンドポイント
        endpoint = `/api/social/follow/${username}`;
        
      }
      
      
      
      // API操作を実行
      try {
        // safeApiCallではなく、直接axiosを使用してトークンを明示する
        const response = await axios({
          method: 'post', // POSTメソッドを明示
          url: `${config.API_BASE_URL}${endpoint}`,
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          data: {} // 空オブジェクトを送信
        });
        
        // サーバーからの応答を確認
        let newFollowState = !currentFollowState; // デフォルトは反転させた状態
        
        // 応答に明示的なフォロー状態がある場合はそれを使用
        if (response && response.data && response.data.is_following !== undefined) {
          newFollowState = Boolean(response.data.is_following);
          
        }
        
        // 操作が成功したのでフォロー状態を確定
        setIsFollowing(newFollowState);
        
        // フォロー状態をグローバルキャッシュに保存
        if (typeof window !== 'undefined' && window.followStateCache) {
          window.followStateCache.set(username, newFollowState);
        }
        
        // プロフィールデータキャッシュも更新
        if (typeof window !== 'undefined' && window.profileDataCache && window.profileDataCache[username]) {
          window.profileDataCache[username] = {
            ...window.profileDataCache[username],
            is_following: newFollowState,
            isFollowing: newFollowState
          };
        }
        
        
      } catch (apiError) {
        
        
        // APIエラー応答を詳細にログ出力
        if (apiError.response) {
          
          
          // 400/409エラー: すでにフォロー中またはアンフォロー済みの可能性
          if (apiError.response.status === 400 || apiError.response.status === 409) {
            // エラーメッセージから状態を推測
            const errorMsg = apiError.response.data?.error || apiError.response.data?.message || '';
            
            if (errorMsg.toLowerCase().includes('already') && errorMsg.toLowerCase().includes('follow')) {
              // "already following"のようなメッセージの場合、フォロー中と判断
              
              setIsFollowing(true);
              
              // キャッシュも更新
              if (typeof window !== 'undefined') {
                if (window.followStateCache) window.followStateCache.set(username, true);
                if (window.profileDataCache && window.profileDataCache[username]) {
                  window.profileDataCache[username].is_following = true;
                }
              }
              
              return; // 早期リターン
            } else if (errorMsg.toLowerCase().includes('not') && errorMsg.toLowerCase().includes('follow')) {
              // "not following"のようなメッセージの場合、フォロー解除済みと判断
              
              setIsFollowing(false);
              
              // キャッシュも更新
              if (typeof window !== 'undefined') {
                if (window.followStateCache) window.followStateCache.set(username, false);
                if (window.profileDataCache && window.profileDataCache[username]) {
                  window.profileDataCache[username].is_following = false;
                }
              }
              
              return; // 早期リターン
            }
          }
          
          // 認証エラーの場合はログイン画面へ
          if (apiError.response.status === 401) {
            navigate('/login');
            return;
          }
        }
        
        // その他のエラー: 元の状態に戻す
        setIsFollowing(currentFollowState);
        
        // エラーメッセージを表示
        
      }
    } catch (error) {
      
      // 元の状態に戻す
      setIsFollowing(isFollowing);
    } finally {
      setIsFollowLoading(false);
    }
  };

  // 相対時間フォーマット
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    // タイムスタンプを明示的にUTCとして解釈し、日本時間に変換
    return moment.utc(timestamp).tz('Asia/Tokyo').fromNow();
  };

  // HTML形式のテキストを安全に表示
  const renderTextWithMentions = () => {
    // 空のテキストの場合のレンダリング
    if (!postText) {
      return <span className="post__text-empty">投稿内容がありません</span>;
    }
    
    // メンションとハッシュタグ付きのテキストをフォーマット
    const textParts = formatTextWithMentionsAndHashtags(postText, navigate);
    
    // 処理したテキストパーツが空の場合
    if (!textParts || textParts.length === 0) {
      return <span>{postText}</span>;
    }
    
    // パーツ処理用の関数
    const renderPart = (part) => {
      if (part.type === 'text') {
        // 通常のテキスト部分
        return <span key={part.key}>{part.content}</span>;
      } else if (part.type === 'mention') {
        // メンション部分: aタグとしてリンク化
        return (
          <a
            key={part.key}
            className="post__mention"
            href={`/profile/${part.username}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              checkTokenBeforeNavigate(`/profile/${part.username}`);
            }}
            style={{ 
              color: '#1DA1F2', 
              textDecoration: 'none',
              fontWeight: '500'
            }}
          >
            {part.content}
          </a>
        );
      } else if (part.type === 'hashtag') {
        // ハッシュタグ部分: aタグとしてリンク化
        return (
          <a
            key={part.key}
            className="post__hashtag"
            href={`/explore?hashtag=${encodeURIComponent(part.rawHashtag)}`}
            onClick={(e) => handleHashtagClick(e, part.rawHashtag)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleHashtagClick(e, part.rawHashtag);
              }
            }}
            style={{ 
              color: '#1DA1F2', 
              textDecoration: 'none',
              fontWeight: '500'
            }}
          >
            {part.content}
          </a>
        );
      } else if (part.type === 'emoji') {
        // 絵文字部分: .emojiクラス付きspanで囲む
        return (
          <span key={part.key} className="emoji">
            {part.content}
          </span>
        );
      }
      return null;
    };
    
    // すべてのパーツをレンダリング
    return <>{textParts.map(renderPart)}</>;
  };

  // ピン留め処理
  const handlePinPost = async (e) => {
    // イベントがある場合、伝播を停止
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!isLoggedIn || !currentUser) {
      navigate('/login');
      return;
    }

    try {
      setIsPinning(true);
      handleMenuClose();

      // トークンを取得（AuthContextと同じキーを使用）
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        console.error('認証トークンが見つかりません');
        navigate('/login');
        return;
      }
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };

      if (isPinnedPost) {
        await axios.delete(`${API_BASE_URL}api/users/profile/pinned/${id}`, { headers });
        setIsPinnedPost(false);
        
        // ピン留め解除イベントを発行
        const unpinnedEvent = new CustomEvent('post_unpinned', {
          detail: {
            postId: id,
            postAuthor: {
              username: postAuthor.username,
              displayName: postAuthor.displayName,
              avatar: postAuthor.avatar,
              verified: postAuthor.verified
            }
          }
        });
        window.dispatchEvent(unpinnedEvent);
      } else {
        await axios.post(`${API_BASE_URL}api/users/profile/pinned/${id}`, {}, { headers });
        setIsPinnedPost(true);
        
        // ピン留めイベントを発行
        const pinnedEvent = new CustomEvent('post_pinned', {
          detail: {
            postId: id,
            postAuthor: {
              username: postAuthor.username,
              displayName: postAuthor.displayName,
              avatar: postAuthor.avatar,
              verified: postAuthor.verified
            }
          }
        });
        window.dispatchEvent(pinnedEvent);
      }

      savePostStateToCache(id, { isPinnedPost: !isPinnedPost });
    } catch (error) {
      
    } finally {
      setIsPinning(false);
    }
  };

  // シェア機能
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${postAuthor.displayName}さんの投稿`,
        text: text,
        url: `${window.location.origin}/post/${id}`
      })
      .catch((error) => {  });
    } else {
      // クリップボードにリンクをコピー
      navigator.clipboard.writeText(`${window.location.origin}/post/${id}`)
        .then(() => {
          // コピー成功の通知があればここに追加
          
          
          // イベントディスパッチャがあれば実行
          if (typeof window !== 'undefined' && window.dispatchEvent) {
            const event = new CustomEvent('notification', { 
              detail: { 
                message: 'リンクをコピーしました', 
                type: 'success'
              } 
            });
            window.dispatchEvent(event);
          }
        })
        .catch(err => {
          
        });
    }
  };

  // リツイート情報を正しく表示する関数
  const formatRetweeter = (retweeter) => {
    if (!retweeter) return '';
    
    // オブジェクトの場合は適切なプロパティを取得
    if (typeof retweeter === 'object') {
      return retweeter.displayName || retweeter.display_name || retweeter.username || '不明なユーザー';
    }
    
    // 文字列の場合はそのまま返す
    return retweeter;
  };

  // プロフィールカードのレンダリング
  const renderProfileCard = () => {
    if (!showProfileCard || !profileData) return null;
    
    return (
      <div 
        className="profile-card"
        style={{
          position: 'absolute', // 絶対位置に変更
          top: `${profileCardPosition.top}px`,
          left: `${profileCardPosition.left}px`
        }}
        onMouseEnter={() => {
          if (hideCardTimeoutId) {
            clearTimeout(hideCardTimeoutId);
            setHideCardTimeoutId(null);
          }
        }}
        onMouseLeave={hideProfileCard}
      >
        <div className="profile-card__header">
          <Avatar 
            src={normalizeImageUrl(profileData.avatar)} 
            alt={profileData.display_name || profileData.username} 
            className="profile-card__avatar"
          />
          <div className="profile-card__user-info">
            <h4>
              {(() => {
                // 投稿ユーザーの場合、「源氏名＠店舗名」の形式で表示
                if (profileData.role && 
                   (profileData.role === 'poster' || profileData.role.name === 'poster') && 
                   profileData.poster_profile) {
                  const { poster_profile } = profileData;
                  
                  // 源氏名と店舗名がある場合
                  if (poster_profile.stage_name && poster_profile.shop_name) {
                    return `${poster_profile.stage_name}＠${poster_profile.shop_name}`;
                  } 
                  // 源氏名のみある場合
                  else if (poster_profile.stage_name) {
                    return poster_profile.stage_name;
                  }
                  // 店舗名のみある場合
                  else if (poster_profile.shop_name) {
                    return `${profileData.username}＠${poster_profile.shop_name}`;
                  }
                }
                // 通常の表示名
                return profileData.display_name || profileData.username;
              })()}
              {profileData.verified && <VerifiedIcon className="profile-card__badge" />}
            </h4>
            <p>@{profileData.username}</p>
          </div>
        </div>
        <div className="profile-card__stats">
          <div className="profile-card__stat">
            <strong>{profileData.following_count || 0}</strong> フォロー中
          </div>
          <div className="profile-card__stat">
            <strong>{profileData.followers_count || 0}</strong> フォロワー
          </div>
        </div>
        <div className="profile-card__bio">
          {profileData.bio || 'プロフィール未設定'}
        </div>
        {isLoggedIn && currentUser && currentUser.username !== username && (
          <div className="profile-card__follow">
            <Button
              variant={isFollowing ? "outlined" : "contained"}
              color="primary"
              fullWidth
              startIcon={isFollowing ? <PersonRemoveIcon /> : <PersonAddIcon />}
              onClick={toggleFollow}
              disabled={isFollowLoading}
            >
              {isFollowing ? 'フォロー中' : '+フォロー'}
            </Button>
          </div>
        )}
        <div className="profile-card__view-profile">
          <Link to={`/profile/${profileData.username}`} onClick={(e) => e.stopPropagation()}>
            プロフィールを表示
          </Link>
        </div>
      </div>
    );
  };

  // トークンの有効性をチェック
  const checkTokenBeforeNavigate = (path) => {
    try {
      // トークンがあるかどうかを確認
      const token = localStorage.getItem('authToken');
      
      // トークンが無効な形式でないかチェック
      if (token && (!token.includes('.') || token.split('.').length !== 3)) {
        console.warn('無効なトークン形式を検出しました。トークンをクリアします。');
        localStorage.removeItem('authToken');
      }
      
      // ナビゲーション実行前に現在のページのURLを取得
      const currentPath = window.location.pathname;
      const currentFullPath = window.location.pathname + window.location.search;
      
      // パスとクエリを含めた完全なURLが同じ場合は何もしない
      if (path === currentFullPath) {
        return;
      }
      
      // 同じベースパスでクエリパラメータのみ異なる場合はpushStateを使用
      if (path.split('?')[0] === currentPath && path.includes('?')) {
        window.history.pushState({}, '', path);
        
        // カスタムイベントをディスパッチして検索コンポーネントに通知
        const searchParams = new URLSearchParams(path.split('?')[1]);
        const query = searchParams.get('q');
        
        if (query && query.startsWith('%23')) {
          const event = new CustomEvent('hashtagSearch', { 
            detail: { 
              query: query,
              rawQuery: decodeURIComponent(query.replace('%23', ''))
            } 
          });
          window.dispatchEvent(event);
        }
        return;
      }
      
      // 通常のナビゲーション
      navigate(path);
    } catch (err) {
      
      // エラーが発生しても可能な限りナビゲート
      navigate(path);
    }
  };

  // ハッシュタグハンドラー関数
  const handleHashtagClick = (e, hashtag) => {
    if (!e || e.defaultPrevented) return;
    
    // イベントの伝播を停止
    e.preventDefault();
    e.stopPropagation();
    
    // 認証トークンを取得
    const token = localStorage.getItem('token');
    
    // ハッシュタグナビゲーション先を/exploreに修正
    // トークンがあれば、URLにトークンも含める（クエリパラメータとして）
    const url = `/explore?hashtag=${encodeURIComponent(hashtag)}${token ? `&token=${token}` : ''}`;
    
    // カスタムイベントでハッシュタグクリックを通知
    // URLが変更された後で検索が実行されるようにするため
    const hashtagEvent = new CustomEvent('hashtagClick', { 
      detail: { hashtag } 
    });
    
    // 1. まずURLを変更
    checkTokenBeforeNavigate(url);
    
    // 2. 少し遅延させてからイベントを発火（ページ遷移が完了してから）
    setTimeout(() => {
      window.dispatchEvent(hashtagEvent);
    }, 100);
  };

  // 投票データがある投稿として記録する関数
  const markPostWithPoll = (postId) => {
    if (!postId) return;
    
    try {
      const numericId = parseInt(postId);
      
      // メモリキャッシュが初期化されていない場合は初期化
      if (!window.postsWithPollCache) {
        window.postsWithPollCache = new Set();
      }
      
      // 1. メモリキャッシュに追加
      window.postsWithPollCache.add(numericId);
      
      // 2. ローカルストレージに保存
      localStorage.setItem(`post_${numericId}_has_poll`, 'true');
      
      // 3. グローバル配列にも追加
      if (!window.POLL_ENABLED_POSTS) {
        window.POLL_ENABLED_POSTS = [];
      }
      
      if (!window.POLL_ENABLED_POSTS.includes(numericId)) {
        window.POLL_ENABLED_POSTS.push(numericId);
      }
      
      // 開発環境のみログ出力
      if (process.env.NODE_ENV === 'development') {
        
      }
    } catch (e) {
      
    }
  };

  // 投票データがある投稿かどうか確認する関数は削除（コンポーネント外部に既に定義済み）

  // 動画の自動再生のための IntersectionObserver の設定
  useEffect(() => {
    if (!video || !videoRef.current) return;

    const options = {
      root: null, // ビューポートをルートとする
      rootMargin: '0px',
      threshold: 0.5, // 50%表示されたら発火
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // videoRef.currentが存在するか確認
        if (!videoRef.current) return;
        
        if (entry.isIntersecting) {
          // 動画が50%以上表示されたら自動再生
          videoRef.current.play().catch(error => {
            // videoRef.currentが存在するか再確認
            if (!videoRef.current) return;
            
            // 自動再生がブロックされた場合は無音で再試行
            if (error.name === 'NotAllowedError') {
              videoRef.current.muted = true;
              videoRef.current.play().catch(e => {  });
            }
          });
        } else {
          // 画面から外れたら一時停止
          // videoRef.currentが存在するか再確認
          if (videoRef.current) {
            videoRef.current.pause();
          }
        }
      });
    }, options);

    observer.observe(videoRef.current);

    return () => {
      observer.disconnect();
    };
  }, [video]);

  // 投稿の本文レンダリングの関数
  const renderContent = () => {
    // 一時データからの取得を試みる
    let tempData = null;
    if (typeof window !== 'undefined' && window.tempPollData && window.tempPollData[id]) {
      tempData = window.tempPollData[id];
    }
    
    // レンダリング内容を事前に計算
    const textContent = (
      <div className="post__text">
        {renderTextWithMentions()}
      </div>
    );
    
    const imageContent = image ? (
      <div className="post__image" onClick={() => setShowImageModal(true)}>
        <img src={normalizeImageUrl(image)} alt="" />
      </div>
    ) : null;
    
    // videoプロパティの存在確認を厳密に行い、様々なケースを考慮する
    let videoContent = null;
    if (video) {
      
      const videoUrl = normalizeImageUrl(video);
      
      
      videoContent = (
        <div className="post__video">
          <video 
            ref={videoRef}
            src={videoUrl} 
            controls 
            controlsList="nodownload nofullscreen" 
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()} 
            loop 
            preload="metadata"
            playsInline
            muted={true}
            defaultmuted
            style={{ width: '100%', maxWidth: '100%', height: 'auto' }} 
            onClick={e => e.stopPropagation()} 
            onError={(e) => {
              
              
              e.target.classList.add("post__video-error");
            }}
          />
        </div>
      );
    }
    
    // 位置情報コンテンツを生成
    const locationContent = postLocation ? (
      <div className="post__location">
        <div className="post__location-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6"/>
          </svg>
        </div>
        <div className="post__location-text">{postLocation}</div>
      </div>
    ) : null;

    // シフト情報コンテンツを生成
    let shiftsContent = null;
    // プロパティのshiftsまたはキャッシュから復元したshiftsを使用
    const cachedState = getPostStateFromCache(id);
    let shiftsData = shifts || (cachedState && cachedState.shifts) || null;
    
    // シフトデータが存在しない場合はAPIから取得を試みる
    if (!shiftsData && id) {
      // キャッシュからのデータが存在しない場合、APIから最新のデータを取得
      const fetchShiftsData = async () => {
        try {
          
          const token = localStorage.getItem('token');
          const response = await axios.get(`${config.API_BASE_URL}/api/posts/${id}?_t=${Date.now()}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          
          if (response && response.data && response.data.shifts) {
            
            shiftsData = response.data.shifts;
            // キャッシュに保存
            savePostStateToCache(id, { shifts: shiftsData });
          }
        } catch (error) {
          
        }
      };
      
      // 非同期処理を開始
      fetchShiftsData();
    }
    
    if (shiftsData && Array.isArray(shiftsData) && shiftsData.length > 0) {
      // 日付順にソート
      const sortedShifts = [...shiftsData].sort((a, b) => {
        return new Date(a.date) - new Date(b.date);
      });
      
      shiftsContent = (
        <div className="post__shifts">
          <h4 className="post__shifts-title">シフト情報</h4>
          {sortedShifts.map((item, index) => {
            const dateObj = new Date(item.date);
            const formattedDate = dateObj instanceof Date && !isNaN(dateObj) 
              ? format(dateObj, 'yyyy年MM月dd日 (EEE)', { locale: ja }) 
              : item.date;
              

              
            return (
              <Paper key={`${item.date}-${index}`} sx={{ p: 1, mb: 1, borderRadius: 1 }} className="post__shift-item">
                <Box sx={{ mb: 1 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
                    {formattedDate}のシフト
                  </Typography>
                  <Box sx={{ ml: 1 }}>
                    {item.shifts && Array.isArray(item.shifts) && item.shifts.map((shift, idx) => (
                      <Box key={`${idx}-${shift.start_time}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, mb: 1 }}>
                        <Typography variant="body1">
                          {shift.start_time} - {shift.end_time}
                          {shift.is_next_day && ' (翌日)'}
                        </Typography>
                        {/* 予約導線は閲覧ユーザー(viewer)と管理者(admin)のみに表示。
                            投稿ユーザー・企業ユーザーには表示しない。 */}
                        {isLoggedIn && currentUser && (currentUser.role === 'viewer' || currentUser.role === 'admin') && authorId && String(currentUser.id) !== String(authorId) && (
                          <Button
                            variant="contained"
                            size="small"
                            sx={{ flexShrink: 0, alignSelf: 'flex-start' }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // 自分のプロフィールの予約タブへ、投稿者・日時をクエリで渡して遷移
                              const params = new URLSearchParams({
                                tab: 'reservations',
                                reserve: '1',
                                poster_id: authorId || '',
                                poster_username: username || '',
                                poster_name: displayName || username || '',
                                poster_avatar: avatar || '',
                                date: item.date || '',
                                start_time: shift.start_time || '',
                              });
                              navigate(`/profile/${currentUser.username}?${params.toString()}`);
                            }}
                          >
                            予約する
                          </Button>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </div>
      );
    }
    
    // 投票コンテンツを生成
    let pollContent = null;
    
    // pollStateDataをpollDataとpollと一時データから選択
    const pollStateData = pollData || tempData || poll || (cachedState && cachedState.pollData);
    
    // この投稿に対して投票のカスタムAPI試行フラグをチェック
    const hasAttemptedPollFetch = id && 
      typeof window !== 'undefined' && 
      window.pollFetchAttempts && 
      window.pollFetchAttempts[id];

    // 投票データが存在しない場合はAPIから取得を試みる - 既に試行済みの場合はスキップ
    if (!pollStateData && id && !isNaN(parseInt(id)) && !hasAttemptedPollFetch) {
      // 無限ループ防止のフラグをウィンドウオブジェクトに設定
      if (typeof window !== 'undefined') {
        window.pollFetchAttempts = window.pollFetchAttempts || {};
        // 試行フラグを設定
        window.pollFetchAttempts[id] = true;
      }
      
      // 非同期で投票データを取得
      const fetchPollData = async () => {
        try {
          const numericPostId = parseInt(id);
          const token = localStorage.getItem('token');
          
          // デバッグ用のエンドポイント試行情報を記録
          if (typeof window !== 'undefined') {
            window.lastAttemptedEndpoints = window.lastAttemptedEndpoints || {};
            window.lastAttemptedEndpoints[numericPostId] = [];
          }
          
          // APIエンドポイントテンプレートを取得（グローバル変数またはデフォルト）
          const endpointTemplates = window.POLL_API_ENDPOINTS || [
            { url: '/api/posts/:id/poll', method: 'GET' }
          ];
          
          // IDを置換して実際のエンドポイントを生成
          const apiEndpoints = endpointTemplates.map(endpoint => {
            const cleanUrl = endpoint.url.replace(':id', numericPostId);
            const fullUrl = `${config.API_BASE_URL}${cleanUrl.startsWith('/') ? cleanUrl : '/' + cleanUrl}`;
            return {
              url: fullUrl,
              method: endpoint.method || 'GET'
            };
          });
          
          let foundValidEndpoint = false;
          let successEndpoint = null;
          let lastError = null; // lastError変数を定義
          
          // 各エンドポイントを順番に試行
          for (const endpoint of apiEndpoints) {
            try {
              // デバッグ用に記録
              if (typeof window !== 'undefined' && window.lastAttemptedEndpoints) {
                window.lastAttemptedEndpoints[numericPostId].push({
                  url: endpoint.url,
                  method: endpoint.method,
                  timestamp: new Date().toISOString()
                });
              }
              
              let response;
              if (endpoint.method === 'GET') {
                response = await axios.get(endpoint.url, {
                  headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                  params: { _t: Date.now() } // キャッシュ回避
                });
              } else if (endpoint.method === 'POST') {
                // POSTメソッドの場合は空のデータを送信
                response = await axios.post(endpoint.url, {}, {
                  headers: token ? { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  } : {
                    'Content-Type': 'application/json'
                  }
                });
              }
              
              if (response && response.data) {
                // APIレスポンスがオブジェクトで、optionsプロパティを持つか確認
                let validPollData = response.data;
                
                // レスポンスがデータラッパーの場合、中身を取り出す
                if (response.data.data && typeof response.data.data === 'object') {
                  validPollData = response.data.data;
                }
                
                // optionsが存在するか、parsableな文字列かを確認
                if (validPollData.options) {
                  let parsedOptions = validPollData.options;
                  
                  // 文字列の場合はJSONとして解析
                  if (typeof validPollData.options === 'string') {
                    try {
                      parsedOptions = JSON.parse(validPollData.options);
                      validPollData.options = parsedOptions;
                    } catch (e) {
                      
                    }
                  }
                  

                  
                  // 成功したエンドポイントを記録
                  successEndpoint = {
                    url: endpoint.url,
                    method: endpoint.method
                  };
                  
                  // グローバル変数に成功したエンドポイントを保存
                  if (typeof window !== 'undefined') {
                    window.successPollEndpoints = window.successPollEndpoints || {};
                    window.successPollEndpoints[numericPostId] = successEndpoint;
                  }
                  
                  setPollData(validPollData);
                  setHasVoted(validPollData.user_vote !== null);
                  savePostStateToCache(id, { 
                    pollData: validPollData,
                    hasVoted: validPollData.user_vote !== null
                  });
                  // 投票データがある投稿としてマーク
                  markPostWithPoll(numericPostId);
                  foundValidEndpoint = true;
                  break;
                }
              }
            } catch (err) {
              // 404エラー (Not Found)や405エラー (Method Not Allowed)の場合は正常とみなす
              if (err.response && (err.response.status === 404 || err.response.status === 405)) {
                // 投票データが存在しない投稿（正常な状態）
                continue; // 次のエンドポイントを試す
              }
              // その他のエラーは記録
              lastError = err;
            }
          }
          
          // 有効なエンドポイントが見つからなかった場合（これは正常な状態）
          if (!foundValidEndpoint) {
            // 投票データが存在しない投稿は正常な状態なので、無効フラグは設定しない
            console.log(`Post ${numericPostId} - 投票データが存在しない投稿（正常）`);
          }
        } catch (error) {
          // 404エラーは投票データが存在しない正常な状態なので、エラーとして扱わない
          if (error.response && error.response.status === 404) {
            console.log(`Post ${id} - 投票データが存在しない投稿（正常）`);
          } else {
            console.error(`Post ${id} - 投票データ取得エラー:`, error);
          }
        }
      };
      
      // 非同期処理を開始 - 少し遅延させて他のリソース読み込みと競合しないようにする
      setTimeout(() => {
        fetchPollData();
      }, 100);
    }
    
    // 投票オプションが存在するか確認
    let hasValidPollOptions = false;
    if (pollStateData && Array.isArray(pollStateData.options) && pollStateData.options.length >= 2) {
      // 各オプションが有効かどうかを検証
      const validOptions = pollStateData.options.filter(option => 
        option && 
        typeof option === 'object' && 
        option.id !== undefined && 
        option.text !== undefined
      );
      
      // 有効なオプションが2つ以上ある場合のみ有効と判断
      hasValidPollOptions = validOptions.length >= 2;
    } else if (pollStateData) {
      // 投票データはあるがオプションが無効な場合、再チェック
      if (pollStateData.options && !Array.isArray(pollStateData.options)) {
        try {
          // optionsが文字列化されたJSONの場合、パースを試みる
          if (typeof pollStateData.options === 'string' && pollStateData.options.startsWith('[')) {
            const parsedOptions = JSON.parse(pollStateData.options);
            if (Array.isArray(parsedOptions) && parsedOptions.length >= 2) {
              // パースに成功した場合、ステートを更新
              pollStateData.options = parsedOptions.map((opt, index) => ({
                id: index + 1,
                text: opt,
                position: index,
                votes: 0
              }));
              hasValidPollOptions = true;
            }
          }
        } catch (e) {
          
        }
      }
    }
    
    // 特定のポストIDが無効な場合（削除された投稿など）
    if (window.INVALID_POST_IDS && window.INVALID_POST_IDS.has(parseInt(id))) {
      hasValidPollOptions = false;
    }
    
    if (hasValidPollOptions) {
      // 強制的に投票内容を表示する関数
      try {
        pollContent = (
          <div className="post__poll">
            {pollStateData.options.map((option, index) => {
              // 投票後または投票期限終了後、またはログインしていない場合は常に結果を表示
              const percentage = calculatePercentage(option.votes || 0, pollStateData.total_votes || 0);
              const isSelected = pollStateData.user_vote === option.id;
              
              return (
                <div 
                  key={option.id || `poll-option-${index}`}
                  className={`post__poll-option ${isSelected ? 'post__poll-option--selected' : ''} ${hasVoted || pollStateData.is_closed ? 'post__poll-option--voted' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    
                    // 未ログインの場合はログインページへリダイレクト
                    if (!isLoggedIn) {
                      navigate('/login', { state: { from: location.pathname } });
                      return;
                    }
                    
                    // 既に投票済みまたは投票期限が終了している場合も含めて条件を緩和
                    if (pollStateData.is_closed) {
                      
                      toast.info('この投票は既に終了しています');
                      return;
                    }
                    
                    // 投票ハンドラーを呼び出し
                    handleVote(option.id);
                  }}
                >
                  {/* 投票済みアイコンを表示（選択されている場合のみ） */}
                  {isSelected && (
                    <div className="post__poll-option-voted-icon">
                      <HowToVoteIcon fontSize="small" />
                    </div>
                  )}
                  
                  <div className="post__poll-option-text">
                    {option.text}
                  </div>
                  
                  {/* 結果表示部分 - すべてのユーザーに表示 */}
                  <div className="post__poll-option-result">
                    <div 
                      className="post__poll-option-bar" 
                      style={{ width: `${percentage}%` }}
                    ></div>
                    <div className="post__poll-option-percentage">
                      {percentage}%
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* 投票フッター部分 */}
            <div className="post__poll-footer">
              <div className="post__poll-votes">
                {pollStateData.total_votes || 0} 票
              </div>
              <div className="post__poll-status">
                {pollStateData.is_closed ? (
                  <span>投票終了</span>
                ) : (
                  <span>投票終了: {moment.utc(pollStateData.end_date).tz('Asia/Tokyo').format('YYYY年MM月DD日 HH:mm')}</span>
                )}
              </div>
              {/* 投票取り消しボタン - ログイン中ユーザーが投票済みで、投票期限が終了していない場合に表示 */}
              {isLoggedIn && (hasVoted || pollStateData.user_vote) && !pollStateData.is_closed && (
                <div className="post__poll-cancel">
                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelVote();
                    }}
                    size="small"
                    variant="outlined"
                    color="secondary"
                    disabled={voteLoading}
                    startIcon={voteLoading ? <CircularProgress size={16} /> : null}
                  >
                    投票を取り消す
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      } catch (error) {
        
        
        // 開発環境では詳細なエラー情報を表示、本番環境ではシンプルなメッセージのみ
        if (process.env.NODE_ENV === 'development') {
          pollContent = (
            <div className="post__poll post__poll--error">
              <div className="post__poll-error">
                <p>投票データの表示中にエラーが発生しました</p>
                <pre>{JSON.stringify({ error: error.message, pollData: pollStateData }, null, 2)}</pre>
              </div>
            </div>
          );
        } else {
          // 本番環境ではシンプルにする
          pollContent = null;
        }
      }
    }
    
    return (
      <div className="post__content">
        {textContent}
        {locationContent}
        {imageContent}
        {videoContent}
        {pollContent}
        {shiftsContent}
      </div>
    );
  };
  
  // コメント表示レンダリング関数
  const renderComments = () => {
    // コメントが存在しない場合
    if (!commentsList || commentsList.length === 0) {
      if (loadingComments) {
        return <div className="post__comments-loading">コメントを読み込み中...</div>;
      }
      return <div className="post__no-comments">コメントはありません</div>;
    }

    return (
      <div className="post__comments-list">
        {commentsList.filter(comment => comment && comment.id && comment.user).map(comment => {
          // コメントやユーザープロパティの存在確認
          if (!comment || !comment.user) return null;
          
          // 自分のコメントか投稿者のコメントのみ表示
          const isMyComment = isLoggedIn && currentUser && currentUser.id === comment.user.id;
          const isPostAuthorComment = comment.user.id === authorId;
          
          // 表示条件: 自分のコメントか投稿者のコメントか、自分が投稿者
          const canViewComment = isMyComment || isPostAuthorComment || (isLoggedIn && currentUser && currentUser.id === authorId);
          
          if (!canViewComment) return null;
          
          // 返信の件数（表示のため）
          const repliesCount = commentReplies[comment.id]?.length || comment.replies_count || 0;
          
          return (
            <div key={comment.id} className="post__comment">
              <div className="post__comment-avatar">
                <Avatar 
                  src={normalizeImageUrl(comment.user.avatar)} 
                  alt={comment.user.display_name || comment.user.username || 'ユーザー'}
                  onClick={() => comment.user.username && checkTokenBeforeNavigate(`/profile/${comment.user.username}`)}
                  style={{cursor: 'pointer'}}
                />
              </div>
              <div className="post__comment-content">
                <div className="post__comment-header">
                  <div className="post__comment-user-info">
                    <span 
                      className="post__comment-name"
                      onClick={() => comment.user.username && checkTokenBeforeNavigate(`/profile/${comment.user.username}`)}
                      style={{cursor: 'pointer'}}
                    >
                      {comment.user.display_name || comment.user.username || 'ユーザー'}
                      {comment.user.verified && <VerifiedIcon className="post__comment-badge" />}
                    </span>
                    {comment.user.username && (
                      <span 
                        className="post__comment-username"
                        onClick={() => checkTokenBeforeNavigate(`/profile/${comment.user.username}`)}
                        style={{cursor: 'pointer'}}
                      >
                        @{comment.user.username}
                      </span>
                    )}
                    <span className="post__comment-time">・{formatTime(comment.created_at)}</span>
                  </div>
                  
                  {/* 自分のコメントのみ削除可能 */}
                  {isMyComment && (
                    <IconButton 
                      className="post__comment-delete" 
                      onClick={() => handleDeleteComment(comment.id)}
                      size="small"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </div>
                <div className="post__comment-text">
                  {comment.text}
                </div>
                <div className="post__comment-actions">
                  {/* 投稿者のみがいいね可能 */}
                  {isLoggedIn && currentUser && currentUser.id === authorId && (
                    <div 
                      className={`post__comment-like ${commentLikes[comment.id] ? 'liked' : ''}`}
                      onClick={() => handleLikeComment(comment.id)}
                      style={{cursor: 'pointer'}}
                    >
                      {commentLikes[comment.id] ? (
                        <FavoriteIcon fontSize="small" color="error" />
                      ) : (
                        <FavoriteBorderIcon fontSize="small" />
                      )}
                    </div>
                  )}
                  
                  {/* 返信ボタンを追加 - 投稿者のみが返信可能 */}
                  {isLoggedIn && currentUser && currentUser.id === authorId && (
                    <div 
                      className="post__comment-reply-btn"
                      onClick={() => toggleReplyMode(comment.id)}
                      style={{cursor: 'pointer', marginLeft: '8px'}}
                    >
                      <ReplyIcon fontSize="small" color={replyToCommentId === comment.id ? 'primary' : 'inherit'} />
                      <span style={{fontSize: '12px', marginLeft: '2px'}}>返信</span>
                    </div>
                  )}
                  
                  {/* 返信表示/非表示ボタン - 返信がある場合のみ表示 */}
                  {repliesCount > 0 && (
                    <div 
                      className="post__comment-show-replies"
                      onClick={() => toggleReplies(comment.id)}
                      style={{cursor: 'pointer', marginLeft: '8px', fontSize: '12px', color: '#1DA1F2'}}
                    >
                      {showReplies[comment.id] ? '返信を隠す' : `${repliesCount}件の返信を表示`}
                    </div>
                  )}
                </div>
                
                {/* 返信フォーム - 返信モードの場合のみ表示 */}
                {replyToCommentId === comment.id && (
                  <div className="post__reply-form" style={{marginTop: '8px'}}>
                    <div className="post__reply-input" style={{display: 'flex', alignItems: 'flex-start'}}>
                      <Avatar 
                        src={isLoggedIn && currentUser ? normalizeImageUrl(currentUser.avatar) : ''} 
                        alt="ユーザー" 
                        style={{width: '32px', height: '32px', marginRight: '8px'}}
                      />
                      <div style={{flexGrow: 1}}>
                        <TextField
                          multiline
                          rows={2}
                          placeholder={`${comment.user.username}さんへ返信...`}
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          fullWidth
                          error={!!replyError}
                          helperText={replyError}
                          variant="outlined"
                          size="small"
                          autoFocus
                        />
                        <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '8px'}}>
                          <Button 
                            onClick={() => setReplyToCommentId(null)}
                            variant="outlined" 
                            color="secondary"
                            size="small"
                            style={{marginRight: '8px'}}
                          >
                            キャンセル
                          </Button>
                          <Button 
                            onClick={() => handlePostReply(comment.id)} 
                            variant="contained" 
                            color="primary"
                            size="small"
                            disabled={isSubmittingReply || !replyText.trim()}
                          >
                            {isSubmittingReply ? <CircularProgress size={24} /> : '返信'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* 返信一覧 - 返信表示状態がtrueの場合のみ表示 */}
                {showReplies[comment.id] && commentReplies[comment.id] && commentReplies[comment.id].length > 0 && (
                  <div className="post__replies-list" style={{marginTop: '8px', paddingLeft: '20px', borderLeft: '2px solid #eee'}}>
                    {commentReplies[comment.id].map(reply => {
                      // 自分の返信か投稿者の返信のみが削除可能
                      const isMyReply = isLoggedIn && currentUser && currentUser.id === reply.user.id;
                      
                      return (
                        <div key={reply.id} className="post__reply" style={{marginBottom: '12px'}}>
                          <div style={{display: 'flex', alignItems: 'flex-start'}}>
                            <Avatar 
                              src={normalizeImageUrl(reply.user.avatar)} 
                              alt={reply.user.display_name || reply.user.username || 'ユーザー'}
                              style={{width: '28px', height: '28px', marginRight: '8px'}}
                              onClick={() => reply.user.username && checkTokenBeforeNavigate(`/profile/${reply.user.username}`)}
                            />
                            <div style={{flexGrow: 1}}>
                              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <div>
                                  <span 
                                    style={{fontWeight: 'bold', cursor: 'pointer'}}
                                    onClick={() => reply.user.username && checkTokenBeforeNavigate(`/profile/${reply.user.username}`)}
                                  >
                                    {reply.user.display_name || reply.user.username || 'ユーザー'}
                                    {reply.user.verified && <VerifiedIcon style={{width: '14px', height: '14px', marginLeft: '2px'}} />}
                                  </span>
                                  <span style={{color: '#657786', fontSize: '12px', marginLeft: '4px'}}>
                                    @{reply.user.username} ・{formatTime(reply.created_at)}
                                  </span>
                                </div>
                                {isMyReply && (
                                  <IconButton 
                                    onClick={() => handleDeleteReply(comment.id, reply.id)}
                                    size="small"
                                  >
                                    <DeleteOutlineIcon style={{width: '16px', height: '16px'}} />
                                  </IconButton>
                                )}
                              </div>
                              <div style={{marginTop: '2px'}}>{reply.text}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // 削除済みかどうかのチェックは return 直前に行う
  let content;
  if (isDeleted) {
    content = (
      <div className="post post--deleted" style={{ display: 'none' }}>
      </div>
    );
  } else {
    content = (
      <div 
        className={`post ${isDetailPage ? 'post--detail' : ''} ${(isPinnedPost || isPinned) && isProfilePage ? 'post__pinned' : ''}`}
        id={`post-${id}`}
      >
        {retweetInfo.isRetweet && (
          <div className="post__retweet-info">
            <RepeatIcon fontSize="small" />
            <span>{formatRetweeter(retweetInfo.retweeter)}さんがリツイート</span>
          </div>
        )}

        <div className="post__body">
          <div className="post__header">
            <div 
              className="post__avatar" 
              onClick={handleAvatarClick}
              onMouseEnter={showProfileCardHandler}
              onMouseLeave={hideProfileCard}
              ref={avatarRef}
            >
              <Avatar src={normalizeImageUrl(postAuthor.avatar)} />
            </div>
            <div className="post__header-text">
              <div className="post__author-info">
                <div className="post__author-name-group">
                  <h3 
                    onClick={(e) => {
                      e.stopPropagation();
                      checkTokenBeforeNavigate(`/profile/${postAuthor.username}`);
                    }}
                    className="post__displayName"
                  >
                    {postAuthor.displayName}
                    {postAuthor.verified && <VerifiedIcon className="post__badge" />}
                  </h3>
                  <span 
                    onClick={(e) => {
                      e.stopPropagation();
                      checkTokenBeforeNavigate(`/profile/${postAuthor.username}`);
                    }}
                    className="post__username"
                  >
                    @{postAuthor.username}
                  </span>
                  <span className="post__headerDot">·</span>
                  <span className="post__headerTime">{formatTime(createdAt)}</span>
                </div>
                <div className="post__options">
                  <IconButton 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleMenuOpen(e);
                    }} 
                    size="small"
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            </div>
          </div>
          
                    <div className="post__content-wrapper" onClick={handlePostClick}>
            {renderContent()}
          </div>
          
          <div className="post__actions">
            {/* フッターアイコン */}
            {/* コメントアイコン - hideCommentIconがtrueの場合は非表示 */}
            {!hideCommentIcon && (
              <div className="post__action post__action--comment">
                <div className="post__action-container">
                  <IconButton 
                    onClick={toggleCommentForm} 
                    size="small"
                    disabled={isGuest}
                  >
                    {isGuest ? <LockIcon fontSize="small" /> : <ChatBubbleOutlineIcon fontSize="small" />}
                  </IconButton>
                  <span>
                    {commentsCount > 0 ? commentsCount : ''}
                  </span>
                </div>
              </div>
            )}
            
            {/* リツイートアイコン - hideRetweetIconがtrueの場合またはゲスト・閲覧ユーザーの場合は非表示 */}
            {!hideRetweetIcon && !isGuestOrViewer && (
              <div 
                className={`post__action post__action--retweet ${retweeted ? 'retweeted' : ''}`} 
                data-buttontype="retweet"
                data-retweeted={retweeted}
              >
                <div className="post__action-container">
                  <IconButton 
                    onClick={handleRetweet} 
                    size="small"
                    disabled={!canRetweet || retweetLoading}
                  >
                    {retweetLoading ? <CircularProgress size={20} /> : <RepeatIcon fontSize="small" />}
                  </IconButton>
                  <span>
                    {retweetCount > 0 ? retweetCount : ''}
                  </span>
                </div>
              </div>
            )}
            
            <div className={`post__action post__action--like ${liked ? 'liked' : ''}`}>
              <div className="post__action-container">
                <IconButton 
                  onClick={handleLike} 
                  size="small"
                  disabled={isGuest || likeLoading}
                >
                  {likeLoading ? (
                    <CircularProgress size={20} />
                  ) : isGuest ? (
                    <LockIcon fontSize="small" />
                  ) : (
                    liked ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />
                  )}
                </IconButton>
                <span>
                  {likeCount > 0 ? likeCount : ''}
                </span>
              </div>
            </div>
            
            <div className={`post__action post__action--bookmark ${bookmarked ? 'bookmarked' : ''}`}>
              <div className="post__action-container">
                <IconButton 
                  onClick={handleBookmark} 
                  size="small"
                  disabled={isGuest || bookmarkLoading}
                >
                  {bookmarkLoading ? (
                    <CircularProgress size={20} />
                  ) : isGuest ? (
                    <LockIcon fontSize="small" />
                  ) : (
                    bookmarked ? <BookmarkIcon fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />
                  )}
                </IconButton>
              </div>
            </div>

            <div className="post__action post__action--share">
              <div className="post__action-container">
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShare();
                  }} 
                  size="small"
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              </div>
            </div>
          </div>
          
          {/* コメントフォーム */}
          {showCommentForm && (
            <div className="post__comment-form">
              <div className="post__comment-input">
                <Avatar 
                  src={isLoggedIn && currentUser ? normalizeImageUrl(currentUser.avatar) : ''} 
                  alt="ユーザー" 
                  className="post__comment-avatar"
                />
                <TextField
                  multiline
                  rows={2}
                  placeholder="コメントを入力..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  fullWidth
                  error={!!commentError}
                  helperText={commentError}
                  variant="outlined"
                />
              </div>
              <div className="post__comment-actions">
                <Button 
                  onClick={() => setShowCommentForm(false)}
                  variant="outlined" 
                  color="secondary"
                  size="small"
                >
                  キャンセル
                </Button>
                <Button 
                  onClick={handlePostComment} 
                  variant="contained" 
                  color="primary"
                  size="small"
                  disabled={isSubmitting || !commentText.trim()}
                >
                  {isSubmitting ? <CircularProgress size={24} /> : '投稿'}
                </Button>
              </div>
            </div>
          )}
          
          {/* プロフィールカードは.post直下に配置 */}
          {renderProfileCard()}
          
          {/* コメント表示ボタンと表示領域 - コメント数があるとき表示 */}
          {commentsCount > 0 && (
            <div className="post__comments-section">
              <div className="post__show-comments-btn" onClick={toggleComments}>
                {showComments ? "コメントを隠す" : "コメントを表示"}
                {showComments ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </div>
              
              {/* コメント表示領域 */}
              {showComments && (
                <div className="post__comments">
                  {renderComments()}
                </div>
              )}
            </div>
          )}
          
          {/* 画像モーダル */}
          {showImageModal && (
            <div 
              className="image-modal-overlay" 
              onClick={() => setShowImageModal(false)}
            >
              <div className="image-modal" onClick={e => e.stopPropagation()}>
                <img src={normalizeImageUrl(image)} alt="投稿画像（拡大）" />
                <IconButton 
                  className="image-modal-close" 
                  onClick={() => setShowImageModal(false)}
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
          )}

          {/* オプションメニュー */}
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl) && menuOpen}
            onClose={handleMenuClose}
            onClick={(e) => e.stopPropagation()}
            PaperProps={{
              style: {
                boxShadow: '0px 5px 15px rgba(0,0,0,0.2)',
                borderRadius: '12px',
                minWidth: '200px'
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {isPostOwner && (
              <MenuItem onClick={handleDeletePost} className="post__menuItem post__menuItem--delete">
                <DeleteOutlineIcon className="post__menuIcon" />
                削除
              </MenuItem>
            )}
            
            {/* 管理者/モデレーター用の削除オプション */}
            {!isPostOwner && isLoggedIn && currentUser && (currentUser.role === 'admin' || currentUser.role === 'moderator') && (
              <MenuItem onClick={handleDeletePost} className="post__menuItem post__menuItem--admin">
                <DeleteOutlineIcon className="post__menuIcon" />
                管理者として削除
              </MenuItem>
            )}
            
            {isPostOwner && (
              <MenuItem onClick={handlePinPost} className="post__menuItem">
                {isPinnedPost ? 
                  <span className="post__menuIcon material-icons-outlined">push_pin_off</span> : 
                  <span className="post__menuIcon material-icons-outlined">push_pin</span>
                }
                {isPinnedPost ? 'ピン留め解除' : 'プロフィールにピン留め'}
              </MenuItem>
            )}
            
            <MenuItem onClick={() => navigate(`/report?user=${username}`)} className="post__menuItem">
              <FlagIcon className="post__menuIcon" />
              投稿を報告
            </MenuItem>
          </Menu>
        </div>
      </div>
    );
  }

  return content;
}

export default Post;